import firebase from 'firebase/app';
import 'firebase/firestore';
import 'firebase/database';
import _ from 'lodash';
import format from 'date-fns/format';
import { getQuarter } from 'date-fns';
import { pgQuarter, emptySnap } from './engagementPgApi';
import { getUserAll } from './core/cacheService';
import { resolveTeamHeightFromTeams } from '@/utils/teamHeight';
import { resolveGoalAlignmentDisplay } from './utils/goalAlignment';

// Helper to get all descendant teams (children, grandchildren, etc.) of a team
function getAllDescendantTeams(teamId: string, teamsAll: any[], descendants: string[] = [], visited: Set<string> = new Set()): string[] {
  if (visited.has(teamId)) {
    return descendants;
  }
  visited.add(teamId);

  // Find all teams where parent equals the current teamId
  for (const team of teamsAll) {
    const childId = team.key || team.id;
    if (team.parent === teamId && !visited.has(childId)) {
      descendants.push(childId);
      getAllDescendantTeams(childId, teamsAll, descendants, visited);
    }
  }

  return descendants;
}

// Helper to find the root team (topmost parent team with no parent or empty parent)
function findRootTeam(teamsAll: any[]): string | null {
  return resolveTeamHeightFromTeams(teamsAll);
}

async function fetchUsersForCompany(company: string): Promise<{ [key: string]: any }> {
  try {
    const users = await getUserAll(company);
    return users || {};
  } catch (error) {
    console.error('[Quarterly Report] Error fetching users:', error);
    return {};
  }
}

// Helper to fetch custom culture index question IDs from Firebase
async function fetchCultureIndexQuestionIds(company: string): Promise<string[]> {
  try {
    const snapshot = await firebase.database()
      .ref('questions')
      .orderByChild('company')
      .equalTo(company)
      .once('value');

    const questionIds: string[] = [];
    snapshot.forEach((doc: any) => {
      const data = doc.val();
      if (data.culture_index === true && data.report_card === 'Y') {
        questionIds.push(doc.key);
      }
    });
    console.log('[Quarterly Report] Found culture index question IDs:', questionIds);
    return questionIds;
  } catch (error) {
    console.error('[Quarterly Report] Error fetching culture index question IDs:', error);
    return [];
  }
}

export function isQuarterlyActiveGoal(goal: any): boolean {
  return !!(goal && goal.goal_id && !goal.archived);
}

export function buildQuarterlyScopedGoalLookup(
  goalsTeamAI: Record<string, any>,
  scopedTeamIds: string[]
): { totalGoals: number; activeGoalIds: Set<string>; goalNameLookup: { [goalId: string]: { name: string; teamId: string } } } {
  const activeGoalIds = new Set<string>();
  const goalNameLookup: { [goalId: string]: { name: string; teamId: string } } = {};

  scopedTeamIds.forEach((teamId) => {
    const teamGoals = Array.isArray(goalsTeamAI[teamId]) ? goalsTeamAI[teamId] : [];
    teamGoals
      .filter(isQuarterlyActiveGoal)
      .forEach((goal: any) => {
        const goalId = goal.goal_id;
        const goalName = goal.quick_preview || goal.goal_description || goal.title || goal.name || 'Unnamed Goal';

        activeGoalIds.add(goalId);
        goalNameLookup[goalId] = {
          name: goalName,
          teamId
        };
      });
  });

  return {
    totalGoals: activeGoalIds.size,
    activeGoalIds,
    goalNameLookup
  };
}

export function calculateQuarterlyGoalAlignment(
  allFocusItems: any[],
  activeGoalIds: Set<string>
): {
  alignedItems: number;
  unalignedItems: number;
  unknownItems: number;
  alignmentRate: number | null;
} {
  let alignedItems = 0;
  let unalignedItems = 0;

  allFocusItems.forEach((item: any) => {
    const goalId = item.goal_id;

    if (!goalId) {
      return;
    }

    if (goalId === 'unmatched') {
      unalignedItems++;
      return;
    }

    if (!activeGoalIds.has(goalId)) {
      unalignedItems++;
      return;
    }

    alignedItems++;
  });

  const display = resolveGoalAlignmentDisplay(alignedItems, unalignedItems, activeGoalIds.size);

  return {
    alignedItems: display.alignedItems,
    unalignedItems: display.unalignedItems,
    unknownItems: display.unknownItems,
    alignmentRate: display.alignmentRate
  };
}

// Interfaces
interface SlideData {
  id: string;
  type: string;
  data: any;
}

interface MetricValue {
  value: number;
  change: number;
  trend: 'up' | 'down' | 'neutral';
  previousValue: number;
}

interface TeamPerformer {
  teamId: string;
  teamName: string;
  score: number;
  change: number;
}

interface MetricChange {
  metricId: string;
  metricName: string;
  currentValue: number;
  previousValue: number;
  change: number;
}

interface SavedReportMetadata {
  id: string;
  company: string;
  teamId: string;
  teamName: string;
  quarterId: string;
  quarterName: string;
  year: number;
  quarter: number;
  reportName: string;
  createdAt: string;
  updatedAt: string;
  generatedBy: string;
}

interface QuarterlyReportState {
  selectedQuarter: { id: string; name: string; year: number; quarter: number } | null;
  previousQuarter: { id: string; name: string; year: number; quarter: number } | null;
  selectedTeam: string;
  reportData: any;
  slides: SlideData[];
  isLoading: boolean;
  loadingProgress: number;
  currentSlideIndex: number;
  isFullscreen: boolean;
  isReportGenerated: boolean;
  questionLabels: { [key: string]: string };
  questionTypes: { [key: string]: string };
  // Saved reports
  savedReports: SavedReportMetadata[];
  savedReportsLoading: boolean;
  isViewingSavedReport: boolean;
}

// Question set IDs (from report.ts)
const QUESTION_SETS = {
  WHO5: {
    1: '-MdMv_XgThL0o2iunlhE',
    2: '-MdMw8kfpZtxnppGUA1f',
    3: '-MdMyavdF-YvuyzW6cA5',
    4: '-MdMz-TodEXbj3AC68oP',
    5: '-MdMzwfySVTjhOAGoxTP'
  },
  ERES: {
    1: '-MgaSDDhL-3mt78QHuYL',
    2: '-MgaSw_L3eW0WyF4ZplB',
    3: '-MgaTQObIM08enQK5p3F',
    4: '-MgaU6mpwzOgerV7w-IW',
    5: '-MgaUfGLnzhU0BcUv5Db'
  },
  CULTURE: {
    1: '-MjPYFeALtFMAJkTEiFn',
    2: '-MjPZ15ImFhkjDiMI3gH',
    3: '-MjPZVq3sRBZJwtr7_Tx',
    4: '-MjP_2__thuvnVc_RY0k',
    5: '-MjP_R_bWBdQvFhf5BTY'
  },
  DEBI: {
    1: 'DPSRRS',
    2: 'OFRS',
    3: 'OFES',
    4: 'RPRS',
    5: 'VAS'
  },
  STRATEGIC_ALIGNMENT: {
    1: '-OGSfTQqu71fi7f67Ztm',
    2: '-OGSgDcIZeg9vJ6JTUYJ',
    3: '-OGSh6nBCPhmZVVcSvO6',
    4: '-OGSi-yigX4U4lSn8z7h',
    5: '-OGSiV8s4nrPMhxK_zAe',
    6: '-OGSj6iv4BiFfUlwg290',
    7: '-OGSjePG4WFU8A4YH0iB',
    8: '-OGSmPA41e8em6xyRJ-5'
  }
};

const DEBI_WEIGHTS = {
  'DPSRRS': 0.34,
  'OFRS': 0.22,
  'OFES': 0.11,
  'RPRS': 0.22,
  'VAS': 0.11
};

const WHO5_RESPONSE_BUCKETS = [
  { answerKey: 0, scoreWeight: 5 },
  { answerKey: 1, scoreWeight: 4 },
  { answerKey: 2, scoreWeight: 3 },
  { answerKey: 3, scoreWeight: 2 },
  { answerKey: 4, scoreWeight: 1 },
  { answerKey: 5, scoreWeight: 0 }
];

const RECOMMENDATION_SLIDE_LIMITS = {
  maxRecommendations: 3,
  categoryMaxChars: 18,
  titleMaxChars: 72,
  descriptionMaxChars: 190,
  impactMaxChars: 95,
  metricMaxChars: 24
};

const RECOMMENDATION_CONTENT_GUIDELINES = {
  format: 'single 16:9 presentation slide with compact recommendation cards',
  maxRecommendations: RECOMMENDATION_SLIDE_LIMITS.maxRecommendations,
  fields: {
    priority: 'one of high, medium, low',
    category: `1-2 words, max ${RECOMMENDATION_SLIDE_LIMITS.categoryMaxChars} characters`,
    title: `specific action, max ${RECOMMENDATION_SLIDE_LIMITS.titleMaxChars} characters`,
    description: `one concise sentence, max ${RECOMMENDATION_SLIDE_LIMITS.descriptionMaxChars} characters`,
    impact: `short expected outcome, max ${RECOMMENDATION_SLIDE_LIMITS.impactMaxChars} characters`,
    metric: `short label or value, max ${RECOMMENDATION_SLIDE_LIMITS.metricMaxChars} characters`
  },
  instructions: [
    'Return only the strongest three recommendations.',
    'Prefer direct verbs and plain language over detailed explanation.',
    'Do not include paragraphs, bullets, line breaks, markdown, or multi-step plans inside a field.',
    'Make every recommendation fit in a compact card without relying on text wrapping.'
  ]
};

function groupWHO5SnapshotByQuestion(snapshot: any): any {
  const who5QuestionIds = Object.values(QUESTION_SETS.WHO5);
  const grouped: any = {};

  snapshot.forEach((doc: any) => {
    const data = doc.data();
    if (!who5QuestionIds.includes(data.quest_id)) {
      return;
    }

    if (!grouped[data.quest_id]) {
      grouped[data.quest_id] = [];
    }
    grouped[data.quest_id].push(data);
  });

  return grouped;
}

function selectTopLevelTeamRecordsByQuestion(recordsByQuestion: any): any {
  const selected: any = {};

  Object.entries(recordsByQuestion).forEach(([questId, records]: [string, any]) => {
    const teamHierarchy = new Map<string, string | null>();
    const teamData = new Map<string, any>();

    records.forEach((data: any) => {
      const teamId = data.team;
      if (teamId) {
        teamHierarchy.set(teamId, data.parent_team || null);
        teamData.set(teamId, data);
      }
    });

    const topLevelTeams = Array.from(teamHierarchy.keys()).filter(teamId => {
      const parentTeam = teamHierarchy.get(teamId);
      return !parentTeam || !teamHierarchy.has(parentTeam);
    });

    selected[questId] = topLevelTeams
      .map(teamId => teamData.get(teamId))
      .filter(Boolean);
  });

  return selected;
}

function normalizeSlideText(value: any, maxChars: number, fallback = ''): string {
  const text = String(value || fallback).replace(/\s+/g, ' ').trim();
  if (text.length <= maxChars) {
    return text;
  }

  const ellipsis = '...';
  const contentMax = Math.max(maxChars - ellipsis.length, 0);
  const truncated = text.slice(0, contentMax + 1);
  const lastSpaceIndex = truncated.lastIndexOf(' ');
  const cutoff = lastSpaceIndex > Math.floor(contentMax * 0.6) ? lastSpaceIndex : contentMax;

  return `${truncated.slice(0, cutoff).replace(/[.,;:!?-]+$/, '')}${ellipsis}`;
}

function normalizeRecommendationPriority(priority: any): string {
  const normalized = String(priority || 'medium').toLowerCase();
  return ['high', 'medium', 'low'].includes(normalized) ? normalized : 'medium';
}

function normalizeGeneratedRecommendations(recommendations: any[]): any[] {
  if (!Array.isArray(recommendations)) {
    return [];
  }

  return recommendations.slice(0, RECOMMENDATION_SLIDE_LIMITS.maxRecommendations).map((recommendation: any) => {
    const source = recommendation || {};

    return {
      priority: normalizeRecommendationPriority(source.priority),
      category: normalizeSlideText(source.category, RECOMMENDATION_SLIDE_LIMITS.categoryMaxChars, 'Engagement'),
      title: normalizeSlideText(source.title, RECOMMENDATION_SLIDE_LIMITS.titleMaxChars, 'Focus on the highest-risk area'),
      description: normalizeSlideText(source.description, RECOMMENDATION_SLIDE_LIMITS.descriptionMaxChars, 'Take one focused action against the lowest-scoring signal this quarter.'),
      impact: source.impact
        ? normalizeSlideText(source.impact, RECOMMENDATION_SLIDE_LIMITS.impactMaxChars)
        : source.impact,
      metric: source.metric
        ? normalizeSlideText(source.metric, RECOMMENDATION_SLIDE_LIMITS.metricMaxChars)
        : source.metric
    };
  });
}

async function fetchWHO5ReportCardData(db: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }, quarterId: string): Promise<any> {
  const baseQuery = () => db.collection('insight_teams_report_card')
    .where('company', '==', params.company)
    .where('quarter', '==', Number(quarterId));

  if (params.team === 'company-wide') {
    const snapshot = await baseQuery().get();
    return selectTopLevelTeamRecordsByQuestion(groupWHO5SnapshotByQuestion(snapshot));
  }

  const [directTeamSnapshot, parentTeamSnapshot] = await Promise.all([
    baseQuery().where('team', '==', params.team).get(),
    baseQuery().where('parent_team', '==', params.team).get()
  ]);

  const directData = groupWHO5SnapshotByQuestion(directTeamSnapshot);
  const parentData = groupWHO5SnapshotByQuestion(parentTeamSnapshot);
  const selected: any = {};

  Object.values(QUESTION_SETS.WHO5).forEach((questionId: any) => {
    selected[questionId] = directData[questionId] && directData[questionId].length > 0
      ? directData[questionId]
      : (parentData[questionId] || []);
  });

  return selected;
}

export default {
  namespaced: true,
  state: {
    selectedQuarter: null,
    previousQuarter: null,
    selectedTeam: 'company-wide',
    reportData: null,
    slides: [],
    isLoading: false,
    loadingProgress: 0,
    currentSlideIndex: 0,
    isFullscreen: false,
    isReportGenerated: false,
    questionLabels: {},
    questionTypes: {},
    // Saved reports
    savedReports: [],
    savedReportsLoading: false,
    isViewingSavedReport: false
  } as QuarterlyReportState,

  mutations: {
    setSelectedQuarter(state: QuarterlyReportState, quarter: any) {
      state.selectedQuarter = quarter;
    },
    setPreviousQuarter(state: QuarterlyReportState, quarter: any) {
      state.previousQuarter = quarter;
    },
    setSelectedTeam(state: QuarterlyReportState, team: string) {
      state.selectedTeam = team;
    },
    setReportData(state: QuarterlyReportState, data: any) {
      state.reportData = data;
    },
    setSlides(state: QuarterlyReportState, slides: SlideData[]) {
      state.slides = slides;
    },
    setLoading(state: QuarterlyReportState, loading: boolean) {
      state.isLoading = loading;
    },
    setLoadingProgress(state: QuarterlyReportState, progress: number) {
      state.loadingProgress = progress;
    },
    setCurrentSlideIndex(state: QuarterlyReportState, index: number) {
      state.currentSlideIndex = index;
    },
    setIsFullscreen(state: QuarterlyReportState, fullscreen: boolean) {
      state.isFullscreen = fullscreen;
    },
    setReportGenerated(state: QuarterlyReportState, generated: boolean) {
      state.isReportGenerated = generated;
    },
    resetReport(state: QuarterlyReportState) {
      state.reportData = null;
      state.slides = [];
      state.currentSlideIndex = 0;
      state.isReportGenerated = false;
      state.loadingProgress = 0;
      state.isViewingSavedReport = false;
    },
    setQuestionLabels(state: QuarterlyReportState, labels: { [key: string]: string }) {
      state.questionLabels = labels;
    },
    setQuestionTypes(state: QuarterlyReportState, types: { [key: string]: string }) {
      state.questionTypes = types;
    },
    // Saved reports mutations
    setSavedReports(state: QuarterlyReportState, reports: SavedReportMetadata[]) {
      state.savedReports = reports;
    },
    setSavedReportsLoading(state: QuarterlyReportState, loading: boolean) {
      state.savedReportsLoading = loading;
    },
    setIsViewingSavedReport(state: QuarterlyReportState, viewing: boolean) {
      state.isViewingSavedReport = viewing;
    }
  },

  actions: {
    // Initialize quarter options
    async initQuarterOptions({ commit, rootState }: any) {
      const dateNow = new Date();
      const thisYear = format(dateNow, 'YYYY');
      const month = parseInt(format(dateNow, 'M'));
      const QuarterOfMonth: { [key: number]: string } = {
        1: '1', 2: '1', 3: '1',
        4: '2', 5: '2', 6: '2',
        7: '3', 8: '3', 9: '3',
        10: '4', 11: '4', 12: '4'
      };

      const currentQuarterId = thisYear + QuarterOfMonth[month];
      const currentQ = parseInt(QuarterOfMonth[month]);

      // Current quarter
      commit('setSelectedQuarter', {
        id: currentQuarterId,
        name: `Q${currentQ} ${thisYear}`,
        year: parseInt(thisYear),
        quarter: currentQ
      });

      // Previous quarter
      let prevQ = currentQ - 1;
      let prevYear = parseInt(thisYear);
      if (prevQ < 1) {
        prevQ = 4;
        prevYear = prevYear - 1;
      }
      commit('setPreviousQuarter', {
        id: `${prevYear}${prevQ}`,
        name: `Q${prevQ} ${prevYear}`,
        year: prevYear,
        quarter: prevQ
      });
    },

    // Fetch question labels and types directly from Firebase
    async fetchQuestionLabels({ commit, rootState }: any) {
      const labels: { [key: string]: string } = {};
      const types: { [key: string]: string } = {};

      try {
        // First try to get from report store if already loaded
        const reportQuestionMaster = (rootState.report && rootState.report.questionMaster) || {};
        const reportQuestionType = (rootState.report && rootState.report.questionType) || {};

        if (Object.keys(reportQuestionMaster).length > 0) {
          Object.assign(labels, reportQuestionMaster);
        }
        if (Object.keys(reportQuestionType).length > 0) {
          Object.assign(types, reportQuestionType);
        }

        // Also fetch directly from Firebase questions collection
        const snapshot = await firebase.database().ref('questions').orderByChild('report_card').equalTo('Y').once('value');
        snapshot.forEach((doc: any) => {
          const data = doc.val();
          const key = doc.key;
          if (data.report_card_label) {
            labels[key] = data.report_card_label;
          }

          // Determine question type
          if (data.nps === 'eNPS') {
            types[key] = 'eNPS';
          } else if (data.nps) {
            types[key] = 'NPS';
          } else if (data.type) {
            types[key] = data.type;
          }
        });

        // Add known metric labels
        labels['DEBI'] = 'DEBI';
        labels['Index'] = 'Strategic Alignment Index';
        labels['Variance'] = 'Alignment Consistency';
        labels['WHO-5'] = 'WHO-5 Wellbeing Index';
        labels['eRes'] = 'eResilience';
        labels['Culture'] = 'Culture Index';

        // Add known metric types
        types['DEBI'] = 'DEBI';
        types['Index'] = 'Index';
        types['Variance'] = 'Variance';
        types['WHO-5'] = 'WHO-5';
        types['eRes'] = 'eRes';
        types['Culture'] = 'Culture';

        commit('setQuestionLabels', labels);
        commit('setQuestionTypes', types);
      } catch (error) {
        console.error('Error fetching question labels:', error);
      }

      return { labels, types };
    },

    // Main action to generate the quarterly report
    async generateQuarterlyReport({ commit, dispatch, state, rootState }: any, params: { quarter: any; team: string }) {
      commit('setLoading', true);
      commit('resetReport');
      commit('setSelectedQuarter', params.quarter);
      commit('setSelectedTeam', params.team);

      // Calculate previous quarter
      let prevQ = params.quarter.quarter - 1;
      let prevYear = params.quarter.year;
      if (prevQ < 1) {
        prevQ = 4;
        prevYear = prevYear - 1;
      }
      commit('setPreviousQuarter', {
        id: `${prevYear}${prevQ}`,
        name: `Q${prevQ} ${prevYear}`,
        year: prevYear,
        quarter: prevQ
      });

      try {
        // Phase 0: Fetch question labels first
        commit('setLoadingProgress', 5);
        await dispatch('fetchQuestionLabels');

        // Determine root team for company-wide queries
        // Use findRootTeam helper to properly identify the top-level parent team
        let rootTeam: string | null = null;
        if (params.team === 'company-wide') {
          // Try to find root team from team store
          const teamsAll = (rootState.team && rootState.team.teamsall) || [];
          if (teamsAll.length > 0) {
            // Use helper function to find the true root team (team with no parent that has children)
            rootTeam = findRootTeam(teamsAll);
          }
          // Alternative: try report store teamkeys (only as fallback)
          if (!rootTeam && rootState.report && rootState.report.teamkeys) {
            // Build teamsAll-like array from teamkeys for root finding
            const teamKeysArray = Object.entries(rootState.report.teamkeys).map(([key, value]: [string, any]) => ({
              key: key,
              parent: value.parent || ''
            }));
            rootTeam = findRootTeam(teamKeysArray);
          }
          console.log('[Quarterly Report] Company-wide selection - Root team identified:', rootTeam);
        }

        // Phase 1: Fetch core metrics (30%)
        commit('setLoadingProgress', 10);
        const coreMetrics = await dispatch('fetchCoreMetrics', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 30);

        // Phase 2: Fetch question scores (50%)
        const questionScores = await dispatch('fetchQuestionScores', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 50);

        // Phase 3: Generate highlights (60%)
        const highlights = await dispatch('generateHighlights', {
          coreMetrics,
          questionScores,
          team: params.team,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 60);

        // Phase 4: Fetch Manager Effectiveness data (70%)
        const managerEffectiveness = await dispatch('fetchManagerEffectivenessData', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 70);

        // Phase 5: Fetch Culture Leaders data (70%)
        const cultureLeaders = await dispatch('fetchCultureLeadersData', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 70);

        // Phase 5b: Fetch Culture Index Question Scores (75%)
        const cultureQuestionScores = await dispatch('fetchCultureQuestionScores', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 75);

        // Phase 6: Fetch WHO-5 Breakdown data (80%)
        const who5Breakdown = await dispatch('fetchWHO5BreakdownData', {
          quarterId: params.quarter.id,
          prevQuarterId: `${prevYear}${prevQ}`,
          team: params.team,
          rootTeam: rootTeam,
          company: rootState.authen.user.company
        });
        commit('setLoadingProgress', 80);

        // Resolve team name from various sources
        let teamName = 'Company-wide';
        if (params.team !== 'company-wide') {
          // Try report store teamkeys first
          if (rootState.report && rootState.report.teamkeys && rootState.report.teamkeys[params.team]) {
            teamName = rootState.report.teamkeys[params.team].name;
          }
          // Try team store teamsall
          else if (rootState.team && rootState.team.teamsall) {
            const teamObj = rootState.team.teamsall.find((t: any) => t.key === params.team);
            if (teamObj) {
              teamName = teamObj.name;
            } else {
              teamName = params.team;
            }
          } else {
            teamName = params.team;
          }
        }

        // Phase 7: Fetch AI Recommendations
        const recommendations = await dispatch('fetchRecommendations', {
          company: rootState.authen.user.company_name || rootState.authen.user.company,
          team: teamName,
          quarterName: params.quarter.name,
          metrics: coreMetrics,
          who5Questions: who5Breakdown.questions || [],
          recognition: {
            total: cultureLeaders.totalRecognitions || 0,
            uniqueRecognizers: cultureLeaders.uniqueRecognizers || 0,
            avgAlignment: cultureLeaders.avgAlignmentScore || 0
          },
          managers: {
            totalConversations: managerEffectiveness.totalConversations || 0,
            avgProactivity: managerEffectiveness.avgProactivity || 0,
            avgPsychSafety: managerEffectiveness.avgPsychSafety || 0,
            activePercent: managerEffectiveness.activePercent || 0
          },
          topStrengths: (highlights.strengths || []).slice(0, 3).map((s: any) => s.question || s.label),
          topChallenges: (highlights.challenges || []).slice(0, 3).map((c: any) => c.question || c.label)
        });
        commit('setLoadingProgress', 85);

        // Phase 8: Fetch Focus & Goal Alignment data (90%)
        const focusAlignment = await dispatch('fetchFocusAlignmentData', {
          teamId: params.team,
          company: rootState.authen.user.company,
          startDate: new Date(params.quarter.year, (params.quarter.quarter - 1) * 3, 1),
          endDate: new Date(params.quarter.year, params.quarter.quarter * 3, 0, 23, 59, 59)
        });
        commit('setLoadingProgress', 90);

        // Compile report data
        const reportData = {
          quarter: params.quarter,
          previousQuarter: state.previousQuarter,
          team: params.team,
          teamName: teamName,
          companyName: rootState.authen.user.company_name || rootState.authen.user.company,
          metrics: coreMetrics,
          questionScores,
          highlights,
          managerEffectiveness,
          cultureLeaders,
          cultureQuestionScores,
          who5Breakdown,
          recommendations,
          focusAlignment,
          generatedAt: new Date().toISOString()
        };

        console.log('[Quarterly Report] Report data compiled:', {
          team: params.team,
          managerEffectivenessTopCount: managerEffectiveness.topManagers ? managerEffectiveness.topManagers.length : 0,
          topGiversCount: cultureLeaders.topGivers ? cultureLeaders.topGivers.length : 0,
          topReceiversCount: cultureLeaders.topReceivers ? cultureLeaders.topReceivers.length : 0
        });

        commit('setReportData', reportData);

        // Phase 6: Generate slides (100%)
        await dispatch('generateSlides', reportData);
        commit('setLoadingProgress', 100);
        commit('setReportGenerated', true);

        // Save the generated report to Firestore
        await dispatch('saveReport', {
          reportData,
          slides: state.slides,
          teamName: teamName,
          quarter: params.quarter,
          company: rootState.authen.user.company,
          generatedBy: rootState.authen.user.email
        });
      } catch (error) {
        console.error('Error generating quarterly report:', error);
      } finally {
        commit('setLoading', false);
      }
    },

    // Fetch core metrics (eNPS, NPS, WHO-5, DEBI, Strategic Alignment)
    async fetchCoreMetrics({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();
      const metrics: any = {
        eNPS: { value: 0, change: 0, trend: 'neutral', previousValue: 0 },
        nps: { value: 0, change: 0, trend: 'neutral', previousValue: 0 },
        who5: { value: 0, change: 0, trend: 'neutral', previousValue: 0 },
        debi: { value: 0, change: 0, trend: 'neutral', previousValue: 0 },
        strategicAlignment: { value: 0, change: 0, trend: 'neutral', previousValue: 0 },
        responseCount: { value: 0, change: 0, trend: 'neutral', previousValue: 0 }
      };

      try {
        // Get questionType from state (populated by fetchQuestionLabels) or fall back to report store
        const questionType = Object.keys(state.questionTypes).length > 0
          ? state.questionTypes
          : ((rootState.report && rootState.report.questionType) || {});

        // Find eNPS and NPS question IDs from questionType
        const eNPSQuestionIds: string[] = [];
        const npsQuestionIds: string[] = [];
        Object.entries(questionType).forEach(([questId, qType]) => {
          if (qType === 'eNPS') {
            eNPSQuestionIds.push(questId);
          } else if (qType === 'NPS') {
            npsQuestionIds.push(questId);
          }
        });

        // Build query for current quarter - use insight_teams_report_card (NOT _fix)
        // The _fix collection is for "most recent" without quarter dimension
        // For quarterly data, use the regular collection with quarter filter
        const teamFilter = params.team === 'company-wide' ? params.rootTeam : params.team;

        let currentQuery = db.collection('insight_teams_report_card')
          .where('company', '==', params.company)
          .where('quarter', '==', Number(params.quarterId));

        if (teamFilter) {
          currentQuery = currentQuery.where('team', '==', teamFilter);
        }

        // Build query for previous quarter
        let prevQuery = db.collection('insight_teams_report_card')
          .where('company', '==', params.company)
          .where('quarter', '==', Number(params.prevQuarterId));

        if (teamFilter) {
          prevQuery = prevQuery.where('team', '==', teamFilter);
        }

        const [currentSnapshot, prevSnapshot] = await Promise.all([
          currentQuery.get(),
          prevQuery.get()
        ]);

        // Process current quarter data
        const currentData: any = {};
        currentSnapshot.forEach((doc: any) => {
          const data = doc.data();
          if (!currentData[data.quest_id]) {
            currentData[data.quest_id] = [];
          }
          currentData[data.quest_id].push(data);
        });

        // Process previous quarter data
        const prevData: any = {};
        prevSnapshot.forEach((doc: any) => {
          const data = doc.data();
          if (!prevData[data.quest_id]) {
            prevData[data.quest_id] = [];
          }
          prevData[data.quest_id].push(data);
        });

        // Calculate eNPS using specific eNPS question IDs
        const eNPSCurrent = calculateNPSScoreByIds(currentData, eNPSQuestionIds);
        const eNPSPrev = calculateNPSScoreByIds(prevData, eNPSQuestionIds);
        metrics.eNPS = {
          value: eNPSCurrent,
          previousValue: eNPSPrev,
          change: eNPSCurrent - eNPSPrev,
          trend: eNPSCurrent > eNPSPrev ? 'up' : eNPSCurrent < eNPSPrev ? 'down' : 'neutral'
        };

        // Calculate NPS using specific NPS question IDs
        const npsCurrent = calculateNPSScoreByIds(currentData, npsQuestionIds);
        const npsPrev = calculateNPSScoreByIds(prevData, npsQuestionIds);
        metrics.nps = {
          value: npsCurrent,
          previousValue: npsPrev,
          change: npsCurrent - npsPrev,
          trend: npsCurrent > npsPrev ? 'up' : npsCurrent < npsPrev ? 'down' : 'neutral'
        };

        // Calculate WHO-5 with the same aggregation rule as the report-card
        // modal: company-wide uses top-level team rows to avoid double-counting
        // child teams, and each stored answer option is scored 0=best to 5=worst.
        const [who5CurrentData, who5PrevData] = await Promise.all([
          fetchWHO5ReportCardData(db, params, params.quarterId),
          fetchWHO5ReportCardData(db, params, params.prevQuarterId)
        ]);
        const who5Current = calculateWHO5Score(who5CurrentData, Object.values(QUESTION_SETS.WHO5));
        const who5Prev = calculateWHO5Score(who5PrevData, Object.values(QUESTION_SETS.WHO5));
        metrics.who5 = {
          value: who5Current,
          previousValue: who5Prev,
          change: Math.round((who5Current - who5Prev) * 10) / 10,
          trend: who5Current > who5Prev ? 'up' : who5Current < who5Prev ? 'down' : 'neutral'
        };

        // Calculate Strategic Alignment Index using category-based weighted calculation (same as Report Card)
        const saiCurrent = calculateStrategicAlignmentScore(currentData, Object.values(QUESTION_SETS.STRATEGIC_ALIGNMENT));
        const saiPrev = calculateStrategicAlignmentScore(prevData, Object.values(QUESTION_SETS.STRATEGIC_ALIGNMENT));
        metrics.strategicAlignment = {
          value: saiCurrent,
          previousValue: saiPrev,
          change: saiCurrent - saiPrev,
          trend: saiCurrent > saiPrev ? 'up' : saiCurrent < saiPrev ? 'down' : 'neutral'
        };

        // Fetch DEBI from engagement_index_quarter
        const debiMetrics = await fetchDEBIMetrics(params);
        metrics.debi = debiMetrics;

      } catch (error) {
        console.error('Error fetching core metrics:', error);
      }

      return metrics;
    },

    // Fetch question scores for detailed breakdown (with previous occurrence comparison)
    // Looks back up to 4 quarters to find the previous time each question was asked
    async fetchQuestionScores({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();
      const questionScores: any[] = [];

      // Helper to generate previous quarter IDs (up to 4 quarters back)
      const getPreviousQuarterIds = (currentQuarterId: string, count: number): string[] => {
        const year = parseInt(currentQuarterId.substring(0, 4));
        const quarter = parseInt(currentQuarterId.substring(4));
        const prevQuarters: string[] = [];

        let y = year;
        let q = quarter;
        for (let i = 0; i < count; i++) {
          q--;
          if (q < 1) {
            q = 4;
            y--;
          }
          prevQuarters.push(`${y}${q}`);
        }
        return prevQuarters;
      };

      try {
        // Build queries for current and previous quarters - use insight_teams_report_card (NOT _fix)
        // Use team filter (rootTeam for company-wide, or selected team)
        const teamFilter = params.team === 'company-wide' ? params.rootTeam : params.team;

        let currentQuery = db.collection('insight_teams_report_card')
          .where('company', '==', params.company)
          .where('quarter', '==', Number(params.quarterId));

        if (teamFilter) {
          currentQuery = currentQuery.where('team', '==', teamFilter);
        }

        // Get up to 4 previous quarter IDs for lookback
        const prevQuarterIds = getPreviousQuarterIds(params.quarterId, 4);

        // Build queries for all previous quarters
        const prevQueries = prevQuarterIds.map(qId => {
          let query = db.collection('insight_teams_report_card')
            .where('company', '==', params.company)
            .where('quarter', '==', Number(qId));
          if (teamFilter) {
            query = query.where('team', '==', teamFilter);
          }
          return query.get();
        });

        // Execute all queries in parallel
        const [currentSnapshot, ...prevSnapshots] = await Promise.all([
          currentQuery.get(),
          ...prevQueries
        ]);

        // Process current quarter data
        const dataByQuestion: any = {};

        currentSnapshot.forEach((doc: any) => {
          const data = doc.data();
          const questId = data.quest_id;
          if (!dataByQuestion[questId]) {
            dataByQuestion[questId] = {
              totalScore: 0,
              count: 0,
              answers: {},
              type: data.type || null,
              nps: data.nps || null,
              questionType: data.question_type || null  // Track question_type for Scale detection
            };
          }

          // Aggregate scores
          if (data.answer) {
            Object.entries(data.answer).forEach(([key, val]: [string, any]) => {
              if (!dataByQuestion[questId].answers[key]) {
                dataByQuestion[questId].answers[key] = 0;
              }
              dataByQuestion[questId].answers[key] += Number(val);
            });
          }

          if (data.percent !== undefined) {
            dataByQuestion[questId].totalScore += data.percent;
            dataByQuestion[questId].count++;
          }

          // Store type info if available
          if (data.type && !dataByQuestion[questId].type) {
            dataByQuestion[questId].type = data.type;
          }
          if (data.nps && !dataByQuestion[questId].nps) {
            dataByQuestion[questId].nps = data.nps;
          }
          if (data.question_type && !dataByQuestion[questId].questionType) {
            dataByQuestion[questId].questionType = data.question_type;
          }
        });

        // Process previous quarters data - organized by quarter then by question
        // Index 0 = most recent previous quarter, index 3 = oldest (4 quarters back)
        const prevDataByQuarter: Array<{ [questId: string]: { totalScore: number; count: number; questionType: string | null; quarterId: string } }> = [];

        prevSnapshots.forEach((snapshot, quarterIndex) => {
          const quarterData: { [questId: string]: { totalScore: number; count: number; questionType: string | null; quarterId: string } } = {};

          snapshot.forEach((doc: any) => {
            const data = doc.data();
            const questId = data.quest_id;
            if (!quarterData[questId]) {
              quarterData[questId] = {
                totalScore: 0,
                count: 0,
                questionType: data.question_type || null,
                quarterId: prevQuarterIds[quarterIndex]
              };
            }
            if (data.percent !== undefined) {
              quarterData[questId].totalScore += data.percent;
              quarterData[questId].count++;
            }
            if (data.question_type && !quarterData[questId].questionType) {
              quarterData[questId].questionType = data.question_type;
            }
          });

          prevDataByQuarter.push(quarterData);
        });

        // Helper to find the most recent previous occurrence of a question
        const findPreviousOccurrence = (questId: string): { totalScore: number; count: number; questionType: string | null; quarterId: string } | null => {
          for (let i = 0; i < prevDataByQuarter.length; i++) {
            const quarterData = prevDataByQuarter[i];
            if (quarterData[questId] && quarterData[questId].count > 0) {
              return quarterData[questId];
            }
          }
          return null;
        };

        // Use question labels and types from state (fetched during report generation)
        const questionLabels = state.questionLabels || {};
        const questionType = Object.keys(state.questionTypes).length > 0
          ? state.questionTypes
          : ((rootState.report && rootState.report.questionType) || {});

        Object.entries(dataByQuestion).forEach(([questId, qData]: [string, any]) => {
          if (qData.count > 0) {
            let avgScore = qData.totalScore / qData.count;
            const label = questionLabels[questId];

            // Skip items without a label (only have ID)
            if (!label || label === questId) {
              return;
            }

            // Determine question type
            let qType = questionType[questId] || qData.type || 'Unknown';
            if (qData.nps === 'eNPS') qType = 'eNPS';
            else if (qData.nps === 'Y' || qData.nps === 'NPS') qType = 'NPS';

            // Check if this is a Scale question (0-10 scale)
            // Scale questions store raw values (0-10), need to convert to percentage (0-100)
            const isScaleQuestion = qData.questionType === 'Scale' || qType === 'Scale';
            if (isScaleQuestion) {
              avgScore = avgScore * 10;  // Convert 0-10 to 0-100
              qType = 'Scale';  // Mark as Scale type
            }
            avgScore = Math.round(avgScore);

            // Calculate change from previous occurrence (look back up to 4 quarters)
            let previousScore: number | null = null;
            let change: number | null = null;
            let trend: 'up' | 'down' | 'neutral' = 'neutral';
            let comparedToQuarter: string | null = null;

            const prevOccurrence = findPreviousOccurrence(questId);
            if (prevOccurrence && prevOccurrence.count > 0) {
              let prevAvg = prevOccurrence.totalScore / prevOccurrence.count;
              // Also convert previous score if it's a Scale question
              if (isScaleQuestion) {
                prevAvg = prevAvg * 10;
              }
              previousScore = Math.round(prevAvg);
              change = avgScore - previousScore;
              trend = change > 0 ? 'up' : change < 0 ? 'down' : 'neutral';
              comparedToQuarter = prevOccurrence.quarterId;
            }

            questionScores.push({
              id: questId,
              label: label,
              type: qType,
              score: avgScore,
              previousScore: previousScore,
              change: change,
              trend: trend,
              answers: qData.answers,
              isScale: isScaleQuestion,  // Track that this is a Scale question for display
              comparedToQuarter: comparedToQuarter  // Track which quarter we're comparing to
            });
          }
        });

        // Sort by score descending
        questionScores.sort((a, b) => b.score - a.score);

      } catch (error) {
        console.error('Error fetching question scores:', error);
      }

      return questionScores;
    },

    // Fetch Culture Index Question Scores (custom questions marked with culture_index: true)
    async fetchCultureQuestionScores({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();
      const cultureQuestionScores: any[] = [];

      try {
        // Step 1: Get custom culture index question IDs
        const cultureQuestionIds = await fetchCultureIndexQuestionIds(params.company);
        if (cultureQuestionIds.length === 0) {
          console.log('[Quarterly Report] No custom culture index questions found');
          return cultureQuestionScores;
        }

        // Helper to generate previous quarter IDs (up to 4 quarters back)
        const getPreviousQuarterIds = (currentQuarterId: string, count: number): string[] => {
          const year = parseInt(currentQuarterId.substring(0, 4));
          const quarter = parseInt(currentQuarterId.substring(4));
          const prevQuarters: string[] = [];

          let y = year;
          let q = quarter;
          for (let i = 0; i < count; i++) {
            q--;
            if (q < 1) {
              q = 4;
              y--;
            }
            prevQuarters.push(`${y}${q}`);
          }
          return prevQuarters;
        };

        // Step 2: Query insight_teams_report_card for these question IDs
        const teamFilter = params.team === 'company-wide' ? params.rootTeam : params.team;

        let currentQuery = db.collection('insight_teams_report_card')
          .where('company', '==', params.company)
          .where('quarter', '==', Number(params.quarterId));

        if (teamFilter) {
          currentQuery = currentQuery.where('team', '==', teamFilter);
        }

        // Get up to 4 previous quarter IDs for lookback
        const prevQuarterIds = getPreviousQuarterIds(params.quarterId, 4);

        // Build queries for all previous quarters
        const prevQueries = prevQuarterIds.map(qId => {
          let query = db.collection('insight_teams_report_card')
            .where('company', '==', params.company)
            .where('quarter', '==', Number(qId));
          if (teamFilter) {
            query = query.where('team', '==', teamFilter);
          }
          return query.get();
        });

        // Execute all queries in parallel
        const [currentSnapshot, ...prevSnapshots] = await Promise.all([
          currentQuery.get(),
          ...prevQueries
        ]);

        // Process current quarter data - only for culture index questions
        const dataByQuestion: any = {};

        currentSnapshot.forEach((doc: any) => {
          const data = doc.data();
          const questId = data.quest_id;

          // Only process if this is a culture index question
          if (!cultureQuestionIds.includes(questId)) {
            return;
          }

          if (!dataByQuestion[questId]) {
            dataByQuestion[questId] = {
              totalScore: 0,
              count: 0,
              questionType: data.question_type || null
            };
          }

          if (data.percent !== undefined) {
            dataByQuestion[questId].totalScore += data.percent;
            dataByQuestion[questId].count++;
          }
          if (data.question_type && !dataByQuestion[questId].questionType) {
            dataByQuestion[questId].questionType = data.question_type;
          }
        });

        // Process previous quarters data
        const prevDataByQuarter: Array<{ [questId: string]: { totalScore: number; count: number; questionType: string | null; quarterId: string } }> = [];

        prevSnapshots.forEach((snapshot, quarterIndex) => {
          const quarterData: { [questId: string]: { totalScore: number; count: number; questionType: string | null; quarterId: string } } = {};

          snapshot.forEach((doc: any) => {
            const data = doc.data();
            const questId = data.quest_id;

            // Only process if this is a culture index question
            if (!cultureQuestionIds.includes(questId)) {
              return;
            }

            if (!quarterData[questId]) {
              quarterData[questId] = {
                totalScore: 0,
                count: 0,
                questionType: data.question_type || null,
                quarterId: prevQuarterIds[quarterIndex]
              };
            }
            if (data.percent !== undefined) {
              quarterData[questId].totalScore += data.percent;
              quarterData[questId].count++;
            }
            if (data.question_type && !quarterData[questId].questionType) {
              quarterData[questId].questionType = data.question_type;
            }
          });

          prevDataByQuarter.push(quarterData);
        });

        // Helper to find the most recent previous occurrence of a question
        const findPreviousOccurrence = (questId: string): { totalScore: number; count: number; questionType: string | null; quarterId: string } | null => {
          for (let i = 0; i < prevDataByQuarter.length; i++) {
            const quarterData = prevDataByQuarter[i];
            if (quarterData[questId] && quarterData[questId].count > 0) {
              return quarterData[questId];
            }
          }
          return null;
        };

        // Use question labels from state
        const questionLabels = state.questionLabels || {};

        // Step 3: Calculate scores for each culture question
        Object.entries(dataByQuestion).forEach(([questId, qData]: [string, any]) => {
          if (qData.count > 0) {
            let avgScore = qData.totalScore / qData.count;
            const label = questionLabels[questId];

            // Skip items without a label
            if (!label || label === questId) {
              return;
            }

            // Check if this is a Scale question (0-10 scale needs conversion to percentage)
            const isScaleQuestion = qData.questionType === 'Scale';
            if (isScaleQuestion) {
              avgScore = avgScore * 10;  // Convert 0-10 to 0-100
            }
            avgScore = Math.round(avgScore);

            // Calculate change from previous occurrence
            let change: number | null = null;

            const prevOccurrence = findPreviousOccurrence(questId);
            if (prevOccurrence && prevOccurrence.count > 0) {
              let prevAvg = prevOccurrence.totalScore / prevOccurrence.count;
              if (isScaleQuestion) {
                prevAvg = prevAvg * 10;
              }
              const previousScore = Math.round(prevAvg);
              change = avgScore - previousScore;
            }

            cultureQuestionScores.push({
              id: questId,
              label: label,
              score: avgScore,
              change: change
            });
          }
        });

        // Sort by score descending
        cultureQuestionScores.sort((a, b) => b.score - a.score);
        console.log('[Quarterly Report] Culture question scores:', cultureQuestionScores);

      } catch (error) {
        console.error('Error fetching culture question scores:', error);
      }

      return cultureQuestionScores;
    },

    // Generate highlights (top performers, improvements, declines)
    async generateHighlights({ state, rootState }: any, params: { coreMetrics: any; questionScores: any[]; team: string; company: string }) {
      const highlights: any = {
        strengths: [],
        challenges: [],
        topPerformers: [],
        biggestImprovements: [],
        biggestDeclines: []
      };

      // Helper to check if item has a valid label (not just an ID)
      const hasValidLabel = (q: any) => q.label && q.label !== q.id && !q.label.startsWith('-');

      // Question IDs to exclude (they have their own dedicated sections/indices)
      const who5QuestionIds = Object.values(QUESTION_SETS.WHO5);
      const eResQuestionIds = Object.values(QUESTION_SETS.ERES);
      const excludedQuestionIds = [...who5QuestionIds, ...eResQuestionIds];

      // Filter by both ID and label patterns
      const isNotExcludedQuestion = (q: any) => {
        // Check by ID (WHO-5 and eResilience questions)
        if (excludedQuestionIds.includes(q.id)) return false;
        // Check by label pattern (backup filter)
        const label = (q.label || '').toLowerCase();
        // WHO-5 patterns
        if (label.includes('world health organization') || label.includes('who-5') || label.includes('well-being index')) return false;
        // eResilience patterns (generic index description)
        if (label.includes('employee resilience index') || label.includes('bounce back from setbacks')) return false;
        return true;
      };
      // Alias for backward compatibility
      const isNotWHO5 = isNotExcludedQuestion;

      // Identify strengths (scores >= 70, exclude NPS types and WHO-5, only items with valid labels)
      params.questionScores.forEach((q: any) => {
        if (q.score >= 70 && q.type !== 'NPS' && q.type !== 'eNPS' && hasValidLabel(q) && isNotWHO5(q)) {
          highlights.strengths.push({
            id: q.id,
            label: q.label,
            score: q.score
          });
        }
      });
      highlights.strengths = highlights.strengths.slice(0, 5);

      // Identify challenges (scores < 50, exclude NPS types and WHO-5, only items with valid labels)
      const challenges = params.questionScores.filter((q: any) =>
        q.score < 50 && q.type !== 'NPS' && q.type !== 'eNPS' && hasValidLabel(q) && isNotWHO5(q)
      );
      highlights.challenges = challenges.slice(0, 5).map((q: any) => ({
        id: q.id,
        label: q.label,
        score: q.score
      }));

      // Combine core metrics AND all question scores with changes for Q-o-Q comparison
      const allChanges: any[] = [];

      // Add core metrics
      const coreMetrics = [
        { name: 'eNPS', type: 'core', ...params.coreMetrics.eNPS },
        { name: 'NPS', type: 'core', ...params.coreMetrics.nps },
        { name: 'WHO-5', type: 'core', ...params.coreMetrics.who5 },
        { name: 'DEBI', type: 'core', ...params.coreMetrics.debi },
        { name: 'Strategic Alignment', type: 'core', ...params.coreMetrics.strategicAlignment }
      ];

      coreMetrics.forEach(m => {
        if (m.change !== 0 && m.change !== null) {
          allChanges.push({
            name: m.name,
            type: m.type,
            currentValue: m.value,
            previousValue: m.previousValue,
            change: m.change
          });
        }
      });

      // Add question scores with changes (excluding NPS types and WHO-5 questions)
      params.questionScores.forEach((q: any) => {
        if (q.change !== null && q.change !== 0 && q.type !== 'NPS' && q.type !== 'eNPS' && isNotWHO5(q)) {
          allChanges.push({
            name: q.label,
            type: 'question',
            currentValue: q.score,
            previousValue: q.previousScore,
            change: q.change,
            comparedToQuarter: q.comparedToQuarter || null
          });
        }
      });

      // Split into improvements and declines
      highlights.biggestImprovements = allChanges
        .filter(m => m.change > 0)
        .sort((a, b) => b.change - a.change)
        .slice(0, 5)
        .map(m => ({
          name: m.name,
          type: m.type,
          currentValue: m.currentValue,
          previousValue: m.previousValue,
          change: m.change,
          comparedToQuarter: m.comparedToQuarter || null
        }));

      highlights.biggestDeclines = allChanges
        .filter(m => m.change < 0)
        .sort((a, b) => a.change - b.change)
        .slice(0, 5)
        .map(m => ({
          name: m.name,
          type: m.type,
          currentValue: m.currentValue,
          previousValue: m.previousValue,
          change: m.change,
          comparedToQuarter: m.comparedToQuarter || null
        }));

      return highlights;
    },

    // Fetch Manager Effectiveness data for the quarter
    async fetchManagerEffectivenessData({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();
      const managerData: any = {
        avgProactivity: 0,
        avgPsychSafety: 0,
        avgDEBI: 0,
        activePercent: 0,
        totalManagers: 0,
        activeManagers: 0,
        topManagers: []
      };

      try {
        // Get quarter date range
        const quarterNum = parseInt(params.quarterId);
        const year = Math.floor(quarterNum / 10);
        const quarter = quarterNum % 10;

        const quarterStartMonth = (quarter - 1) * 3 + 1;
        const startDate = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
        const endMonth = quarter * 3;
        const endDate = `${year}-${String(endMonth).padStart(2, '0')}-${endMonth === 2 ? '28' : ['4', '6', '9', '11'].includes(String(endMonth)) ? '30' : '31'}`;

        console.log('[Quarterly Report] Manager query params:', { quarterId: params.quarterId, year, quarter, startDate, endDate, company: params.company });

        // Query insight_manager_today for manager performance in the quarter
        let managerQuery = db.collection('insight_manager_today')
          .where('company', '==', params.company)
          .where('date', '>=', startDate)
          .where('date', '<=', endDate);

        const managerSnapshot = await managerQuery.get();
        console.log('[Quarterly Report] insight_manager_today query returned:', managerSnapshot.size, 'docs');

        // Aggregate manager data by email
        const managersByEmail: { [email: string]: any } = {};

        managerSnapshot.forEach((doc: any) => {
          const data = doc.data();
          const email = data.email;

          if (!managersByEmail[email]) {
            managersByEmail[email] = {
              email: email,
              name: data.name || email.split('@')[0],
              team: data.team || '',
              teamName: '',
              feedbackReplies: 0,
              feedbackTotal: 0,
              conversations: 0,
              dataPoints: 0
            };
          }

          // Aggregate feedback reply data
          if (data.type === 'feedback') {
            managersByEmail[email].feedbackReplies += Number(data.read || 0);
            managersByEmail[email].feedbackTotal += Number(data.receive || 0);
          }

          managersByEmail[email].dataPoints++;
        });

        // Get team names from team store
        const teamsAll = (rootState.team && rootState.team.teamsall) || [];
        const teamMap: { [key: string]: string } = {};
        teamsAll.forEach((t: any) => {
          teamMap[t.key] = t.name;
        });

        // Get user data for profile pics and team membership
        let users = (rootState.main && rootState.main.users) || {};

        // If users not in store, fetch from Firebase
        if (Object.keys(users).length === 0) {
          users = await fetchUsersForCompany(params.company);
        }

        console.log('[Quarterly Report] Manager data fetched:', Object.keys(managersByEmail).length, 'managers');
        console.log('[Quarterly Report] Users available:', Object.keys(users).length);
        console.log('[Quarterly Report] Team filter:', params.team);

        // Build a set of team IDs to include (selected team + all descendant teams)
        // Note: teamsAll is already defined earlier in this function
        let teamIdsToInclude: Set<string> = new Set();

        if (params.team !== 'company-wide') {
          teamIdsToInclude.add(params.team);
          const descendants = getAllDescendantTeams(params.team, teamsAll);
          descendants.forEach(id => teamIdsToInclude.add(id));
          console.log('[Quarterly Report] Team hierarchy:', { selected: params.team, descendants: descendants.length, totalTeams: teamIdsToInclude.size });
        }

        // Filter managers by team hierarchy
        let filteredManagers = Object.values(managersByEmail);
        if (params.team !== 'company-wide') {
          // First try to filter by the team field in the manager document
          const byDocTeam = filteredManagers.filter((m: any) => teamIdsToInclude.has(m.team));

          // If no matches by document team, try matching via users store
          if (byDocTeam.length === 0 && Object.keys(users).length > 0) {
            const teamMemberEmails = new Set<string>();
            Object.entries(users).forEach(([emailKey, userData]: [string, any]) => {
              if (teamIdsToInclude.has(userData.team)) {
                const email = emailKey.replace(/\|\|/g, '.');
                teamMemberEmails.add(email);
              }
            });
            if (teamMemberEmails.size > 0) {
              filteredManagers = filteredManagers.filter((m: any) => teamMemberEmails.has(m.email));
            } else {
              filteredManagers = []; // No team members found, show empty
            }
          } else {
            filteredManagers = byDocTeam;
          }
          console.log('[Quarterly Report] Managers after team filter:', filteredManagers.length);
        }

        const managers = filteredManagers;
        managerData.totalManagers = managers.length;

        // Fetch DEBI scores for managers based on their team's engagement index
        // DEBI (Dynamic Engagement Behavior Index) measures manager effectiveness in keeping team engaged
        const debiByTeam: { [teamId: string]: number } = {};
        try {
          // Query engagement_index_quarter for the current quarter (PG-first)
          const engagementSnapshot = (await pgQuarter({ company: params.company, quarter: Number(params.quarterId) })) || emptySnap;

          // Group by team
          const engagementByTeam: { [teamId: string]: any[] } = {};
          engagementSnapshot.forEach((doc: any) => {
            const data = doc.data();
            const teamId = data.team || 'unknown';
            if (!engagementByTeam[teamId]) {
              engagementByTeam[teamId] = [];
            }
            engagementByTeam[teamId].push(data);
          });

          // Calculate DEBI for each team
          Object.entries(engagementByTeam).forEach(([teamId, teamData]) => {
            const debiScore = calculateDEBIFromComponents(teamData);
            if (debiScore !== null) {
              debiByTeam[teamId] = Math.round(debiScore);
            }
          });
          console.log('[Quarterly Report] DEBI by team calculated:', Object.keys(debiByTeam).length, 'teams');
        } catch (debiError) {
          console.error('[Quarterly Report] Error fetching DEBI for managers:', debiError);
        }

        managers.forEach((m: any) => {
          // Calculate feedback reply rate (proxy for proactivity)
          const replyRate = m.feedbackTotal > 0 ? (m.feedbackReplies / m.feedbackTotal) * 100 : 0;
          m.proactivityScore = Math.round(replyRate);

          // Psychological safety is estimated from feedback engagement
          // Higher reply rates indicate safer environment for feedback
          m.psychSafetyScore = Math.round(Math.min(100, replyRate * 1.1));

          // Get DEBI score for manager's team
          // First try direct team match, then try to find manager's managed team via users
          m.debiScore = 0;
          if (debiByTeam[m.team]) {
            m.debiScore = debiByTeam[m.team];
          } else {
            // Try to find managed team from users data
            const emailKey = m.email.replace(/\./g, '||');
            if (users[emailKey]) {
              const managedTeam = users[emailKey].team;
              if (managedTeam && debiByTeam[managedTeam]) {
                m.debiScore = debiByTeam[managedTeam];
              }
            }
          }

          // Combined score for ranking - now includes DEBI
          m.combinedScore = (m.proactivityScore * 0.35) + (m.psychSafetyScore * 0.35) + (m.debiScore * 0.30);

          // Resolve team name
          m.teamName = teamMap[m.team] || m.team || 'Unknown';

          // Get user profile info
          const emailKey = m.email.replace(/\./g, '||');
          if (users[emailKey]) {
            m.name = users[emailKey].name || m.name;
            m.profilepic = users[emailKey].profilepic || null;
          }

          // Format name to proper case
          m.name = formatProperCase(m.name);

          // Generate initials
          const nameParts = m.name.split(' ');
          m.initials = nameParts.length > 1
            ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
            : m.name.substring(0, 2).toUpperCase();
        });

        // Filter active managers (those with at least 50% response rate or at least 5 data points)
        const activeManagers = managers.filter((m: any) => m.proactivityScore >= 50 || m.dataPoints >= 5);
        managerData.activeManagers = activeManagers.length;
        managerData.activePercent = managerData.totalManagers > 0
          ? Math.round((activeManagers.length / managerData.totalManagers) * 100)
          : 0;

        // Calculate averages
        if (managers.length > 0) {
          managerData.avgProactivity = Math.round(
            managers.reduce((sum: number, m: any) => sum + m.proactivityScore, 0) / managers.length
          );
          managerData.avgPsychSafety = Math.round(
            managers.reduce((sum: number, m: any) => sum + m.psychSafetyScore, 0) / managers.length
          );
          // Calculate avg DEBI only from managers with DEBI scores
          const managersWithDEBI = managers.filter((m: any) => m.debiScore > 0);
          if (managersWithDEBI.length > 0) {
            managerData.avgDEBI = Math.round(
              managersWithDEBI.reduce((sum: number, m: any) => sum + m.debiScore, 0) / managersWithDEBI.length
            );
          }
        }

        // Get top 5 managers by combined score
        const sortedManagers = [...managers].sort((a: any, b: any) => b.combinedScore - a.combinedScore);
        managerData.topManagers = sortedManagers.slice(0, 5).map((m: any) => ({
          name: m.name,
          team: m.teamName,
          initials: m.initials,
          profilepic: m.profilepic,
          scores: [
            { value: m.proactivityScore, label: 'Proactivity' },
            { value: m.psychSafetyScore, label: 'Psych Safety' },
            { value: m.debiScore, label: 'DEBI' }
          ]
        }));

      } catch (error) {
        console.error('Error fetching manager effectiveness data:', error);
      }

      console.log('[Quarterly Report] Final manager data:', {
        totalManagers: managerData.totalManagers,
        topManagersCount: managerData.topManagers.length,
        avgProactivity: managerData.avgProactivity,
        avgDEBI: managerData.avgDEBI
      });

      return managerData;
    },

    // Fetch Culture Leaders data for the quarter (recognition and alignment)
    async fetchCultureLeadersData({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();
      const cultureData: any = {
        avgAlignmentScore: 0,
        totalRecognitions: 0,
        uniqueRecognizers: 0,
        topGivers: [],
        topReceivers: []
      };

      try {
        // Get quarter date range
        const quarterNum = parseInt(params.quarterId);
        const year = Math.floor(quarterNum / 10);
        const quarter = quarterNum % 10;

        const quarterStartMonth = (quarter - 1) * 3 + 1;
        const startDate = `${year}-${String(quarterStartMonth).padStart(2, '0')}-01`;
        const endMonth = quarter * 3;
        const endDate = `${year}-${String(endMonth).padStart(2, '0')}-${endMonth === 2 ? '28' : ['4', '6', '9', '11'].includes(String(endMonth)) ? '30' : '31'}`;

        // Query here_point collection for recognition data
        let recognitionQuery = db.collection('here_point')
          .where('company', '==', params.company)
          .where('date', '>=', startDate)
          .where('date', '<=', endDate);

        const recognitionSnapshot = await recognitionQuery.get();
        console.log('[Quarterly Report] here_point query returned:', recognitionSnapshot.size, 'docs');

        // Track both givers and receivers separately
        const giversByEmail: { [email: string]: any } = {};
        const receiversByEmail: { [email: string]: any } = {};
        const uniqueSenders = new Set<string>();
        let totalRecognitions = 0;

        // Helper to extract max alignment rating from core_value_ai
        const getMaxAlignment = (coreValueAi: any): number => {
          if (!coreValueAi || typeof coreValueAi !== 'object') return 0;
          let maxRating = 0;
          for (const key in coreValueAi) {
            const item = coreValueAi[key];
            if (item && item.rating) {
              const rating = parseFloat(item.rating);
              if (!isNaN(rating) && rating > maxRating) {
                maxRating = rating;
              }
            }
          }
          return maxRating;
        };

        // Helper to extract the best-aligned core value from core_value_ai
        // The core_value_ai uses numeric indices that map to the hashtag array
        const getBestCoreValue = (coreValueAi: any, hashtags: any): string | null => {
          if (!coreValueAi || typeof coreValueAi !== 'object') return null;
          if (!hashtags) return null;

          let bestIndex: string | null = null;
          let bestRating = 0;

          for (const key in coreValueAi) {
            const item = coreValueAi[key];
            if (item && item.rating) {
              const rating = parseFloat(item.rating);
              if (!isNaN(rating) && rating > bestRating) {
                bestRating = rating;
                bestIndex = key;
              }
            }
          }

          // Map the index to the actual hashtag name
          if (bestIndex !== null && hashtags[bestIndex]) {
            return hashtags[bestIndex];
          }
          return null;
        };

        recognitionSnapshot.forEach((doc: any) => {
          const data = doc.data();
          const recipientEmail = data.send_to_email;
          const senderEmail = data.sender_email;

          if (!recipientEmail || !senderEmail) return;

          totalRecognitions++;
          uniqueSenders.add(senderEmail);

          const maxAlignment = getMaxAlignment(data.core_value_ai);

          // Get the best core value from this recognition
          // hashtag or hashtag_prev contains the value names that map to core_value_ai indices
          const hashtags = data.hashtag || data.hashtag_prev || null;
          const bestValue = getBestCoreValue(data.core_value_ai, hashtags);

          // Track GIVER (sender)
          if (!giversByEmail[senderEmail]) {
            giversByEmail[senderEmail] = {
              email: senderEmail,
              name: data.sender_name || senderEmail.split('@')[0],
              team: '',
              teamName: '',
              count: 0,
              alignmentTotal: 0,
              alignmentCount: 0,
              valueCounts: {} // Track frequency of each value reinforced
            };
          }
          giversByEmail[senderEmail].count++;
          if (maxAlignment > 0) {
            giversByEmail[senderEmail].alignmentTotal += maxAlignment;
            giversByEmail[senderEmail].alignmentCount++;
          }
          if (bestValue) {
            giversByEmail[senderEmail].valueCounts[bestValue] = (giversByEmail[senderEmail].valueCounts[bestValue] || 0) + 1;
          }

          // Track RECEIVER (recipient)
          if (!receiversByEmail[recipientEmail]) {
            receiversByEmail[recipientEmail] = {
              email: recipientEmail,
              name: data.send_to_name || recipientEmail.split('@')[0],
              team: '',
              teamName: '',
              count: 0,
              alignmentTotal: 0,
              alignmentCount: 0,
              valueCounts: {} // Track frequency of each value received
            };
          }
          receiversByEmail[recipientEmail].count++;
          if (maxAlignment > 0) {
            receiversByEmail[recipientEmail].alignmentTotal += maxAlignment;
            receiversByEmail[recipientEmail].alignmentCount++;
          }
          if (bestValue) {
            receiversByEmail[recipientEmail].valueCounts[bestValue] = (receiversByEmail[recipientEmail].valueCounts[bestValue] || 0) + 1;
          }
        });

        cultureData.totalRecognitions = totalRecognitions;
        cultureData.uniqueRecognizers = uniqueSenders.size;

        // Get team names and user data
        const teamsAll = (rootState.team && rootState.team.teamsall) || [];
        const teamMap: { [key: string]: string } = {};
        teamsAll.forEach((t: any) => {
          teamMap[t.key] = t.name;
        });

        let users = (rootState.main && rootState.main.users) || {};

        // If users not in store, fetch from Firebase
        if (Object.keys(users).length === 0) {
          users = await fetchUsersForCompany(params.company);
        }

        console.log('[Quarterly Report] Culture data - Users available:', Object.keys(users).length);

        // Helper to find the top values from valueCounts (up to 3)
        const getTopValues = (valueCounts: { [key: string]: number }): string[] => {
          if (!valueCounts || Object.keys(valueCounts).length === 0) return [];

          // Convert to array and sort by count descending
          const sorted = Object.entries(valueCounts)
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3) // Take top 3
            .map(([value]) => value);

          return sorted;
        };

        // Helper to enrich person data with user info
        const enrichPerson = (person: any) => {
          // Calculate average alignment score
          person.alignmentScore = person.alignmentCount > 0
            ? Math.round((person.alignmentTotal / person.alignmentCount) * 10) / 10
            : 0;

          // Calculate top values (up to 3)
          person.topValues = getTopValues(person.valueCounts);

          // Get user profile info from users store
          const emailKey = person.email.replace(/\./g, '||');
          if (users[emailKey]) {
            // Prefer user store data over recognition data
            person.name = users[emailKey].name || users[emailKey].firstname + ' ' + users[emailKey].lastname || person.name;
            person.profilepic = users[emailKey].profilepic || null;
            person.team = users[emailKey].team || '';
          }

          // Skip if name still looks like an email or team name
          if (person.name.includes('@') || person.name.includes('/')) {
            person.name = person.email.split('@')[0];
          }

          // Format name to proper case
          person.name = formatProperCase(person.name);

          // Resolve team name
          person.teamName = teamMap[person.team] || person.team || 'Unknown';

          // Generate initials
          const nameParts = person.name.split(' ').filter((p: string) => p.length > 0);
          person.initials = nameParts.length > 1
            ? (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase()
            : person.name.substring(0, 2).toUpperCase();

          return person;
        };

        // Enrich all givers and receivers
        const allGivers = Object.values(giversByEmail).map(enrichPerson);
        const allReceivers = Object.values(receiversByEmail).map(enrichPerson);

        console.log('[Quarterly Report] Givers before filter:', allGivers.length);
        console.log('[Quarterly Report] Receivers before filter:', allReceivers.length);

        // Build a set of team IDs to include (selected team + all descendant teams)
        let teamIdsToInclude: Set<string> = new Set();

        if (params.team !== 'company-wide') {
          teamIdsToInclude.add(params.team);
          const descendants = getAllDescendantTeams(params.team, teamsAll);
          descendants.forEach(id => teamIdsToInclude.add(id));
          console.log('[Quarterly Report] Culture leaders team hierarchy:', { selected: params.team, descendants: descendants.length, totalTeams: teamIdsToInclude.size });
        }

        // Filter by team hierarchy if not company-wide
        const filteredGivers = params.team !== 'company-wide'
          ? allGivers.filter((p: any) => teamIdsToInclude.has(p.team))
          : allGivers;
        const filteredReceivers = params.team !== 'company-wide'
          ? allReceivers.filter((p: any) => teamIdsToInclude.has(p.team))
          : allReceivers;

        console.log('[Quarterly Report] Givers after team filter:', filteredGivers.length);
        console.log('[Quarterly Report] Receivers after team filter:', filteredReceivers.length);

        // Recalculate totals for team-filtered data
        if (params.team !== 'company-wide') {
          // Total recognitions = sum of all recognitions received by team members
          const teamTotalReceived = filteredReceivers.reduce((sum: number, p: any) => sum + p.count, 0);
          cultureData.totalRecognitions = teamTotalReceived;
          // Unique recognizers = count of team members who gave recognition
          cultureData.uniqueRecognizers = filteredGivers.length;
        }

        // Helper to format person for leaderboard display
        const formatForLeaderboard = (person: any) => ({
          name: person.name,
          team: person.teamName,
          initials: person.initials,
          profilepic: person.profilepic,
          scores: [
            { value: person.count, label: 'Given' in person ? 'Given' : 'Count' },
            { value: person.alignmentCount > 0 ? person.alignmentScore.toFixed(1) : '-', label: 'Alignment' }
          ]
        });

        // Get top 5 givers by count
        const sortedGivers = [...filteredGivers].sort((a: any, b: any) => b.count - a.count);
        cultureData.topGivers = sortedGivers.slice(0, 5).map((p: any) => ({
          name: p.name,
          team: p.teamName,
          initials: p.initials,
          profilepic: p.profilepic,
          topValues: p.topValues,  // Top reinforced values (up to 3)
          scores: [
            { value: p.count, label: 'Given' },
            { value: p.alignmentCount > 0 ? p.alignmentScore.toFixed(1) : '-', label: 'Alignment' }
          ]
        }));

        // Get top 5 receivers by count
        const sortedReceivers = [...filteredReceivers].sort((a: any, b: any) => b.count - a.count);
        cultureData.topReceivers = sortedReceivers.slice(0, 5).map((p: any) => ({
          name: p.name,
          team: p.teamName,
          initials: p.initials,
          profilepic: p.profilepic,
          topValues: p.topValues,  // Top received values (up to 3)
          scores: [
            { value: p.count, label: 'Received' },
            { value: p.alignmentCount > 0 ? p.alignmentScore.toFixed(1) : '-', label: 'Alignment' }
          ]
        }));

        // Calculate average alignment across all
        const allWithAlignment = [...filteredGivers, ...filteredReceivers].filter((p: any) => p.alignmentCount > 0);
        if (allWithAlignment.length > 0) {
          cultureData.avgAlignmentScore = Math.round(
            (allWithAlignment.reduce((sum: number, p: any) => sum + p.alignmentScore, 0) / allWithAlignment.length) * 10
          ) / 10;
        }

      } catch (error) {
        console.error('Error fetching culture leaders data:', error);
      }

      console.log('[Quarterly Report] Final culture data:', {
        totalRecognitions: cultureData.totalRecognitions,
        topGiversCount: cultureData.topGivers.length,
        topReceiversCount: cultureData.topReceivers.length,
        avgAlignment: cultureData.avgAlignmentScore
      });

      return cultureData;
    },

    // Fetch WHO-5 breakdown data (distribution per question)
    async fetchWHO5BreakdownData({ state, rootState }: any, params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }) {
      const db = firebase.firestore();

      // WHO-5 question labels (standard WHO-5 Wellbeing Index questions)
      const WHO5_QUESTIONS = [
        { id: QUESTION_SETS.WHO5[1], label: 'I have felt cheerful and in good spirits' },
        { id: QUESTION_SETS.WHO5[2], label: 'I have felt calm and relaxed' },
        { id: QUESTION_SETS.WHO5[3], label: 'I have felt active and vigorous' },
        { id: QUESTION_SETS.WHO5[4], label: 'I woke up feeling fresh and rested' },
        { id: QUESTION_SETS.WHO5[5], label: 'My daily life has been filled with things that interest me' }
      ];

      const breakdownData: any = {
        title: 'Wellbeing Breakdown',
        subtitle: 'Response distribution across WHO-5 questions',
        questions: []
      };

      try {
        const [currentData, prevData] = await Promise.all([
          fetchWHO5ReportCardData(db, params, params.quarterId),
          fetchWHO5ReportCardData(db, params, params.prevQuarterId)
        ]);

        // Calculate distribution and score for each WHO-5 question
        WHO5_QUESTIONS.forEach(question => {
          const distribution: { [key: number]: number } = { 0: 0, 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
          let currentScore = 0;
          let currentMax = 0;
          let prevScore = 0;
          let prevMax = 0;

          // Process current quarter
          if (currentData[question.id]) {
            currentData[question.id].forEach((record: any) => {
              if (record.answer && typeof record.answer === 'object') {
                WHO5_RESPONSE_BUCKETS.forEach(bucket => {
                  const count = Number(record.answer[bucket.answerKey] || 0);
                  if (count > 0) {
                    distribution[bucket.answerKey] += count;
                    currentScore += count * bucket.scoreWeight;
                    currentMax += count * 5;
                  }
                });
              }
            });
          }

          // Process previous quarter for comparison
          if (prevData[question.id]) {
            prevData[question.id].forEach((record: any) => {
              if (record.answer && typeof record.answer === 'object') {
                WHO5_RESPONSE_BUCKETS.forEach(bucket => {
                  const count = Number(record.answer[bucket.answerKey] || 0);
                  if (count > 0) {
                    prevScore += count * bucket.scoreWeight;
                    prevMax += count * 5;
                  }
                });
              }
            });
          }

          // Calculate scores (0-100 scale)
          const score = currentMax > 0 ? Math.round((currentScore * 100) / currentMax) : 0;
          const previousScore = prevMax > 0 ? Math.round((prevScore * 100) / prevMax) : 0;
          const change = score - previousScore;

          breakdownData.questions.push({
            label: question.label,
            distribution,
            score,
            previousScore,
            change,
            trend: change > 0 ? 'up' : change < 0 ? 'down' : 'neutral'
          });
        });

      } catch (error) {
        console.error('Error fetching WHO-5 breakdown data:', error);
      }

      return breakdownData;
    },

    // Fetch AI-generated recommendations
    async fetchRecommendations({ state, rootState }: any, params: {
      company: string;
      team: string;
      quarterName: string;
      metrics: any;
      who5Questions: any[];
      recognition: any;
      managers: any;
      topStrengths: string[];
      topChallenges: string[];
    }) {
      const recommendationsData: any = {
        title: 'Key Recommendations',
        subtitle: `AI-generated insights based on ${params.quarterName} data`,
        recommendations: [],
        generatedAt: new Date().toISOString()
      };

      try {
        // Prepare payload for AI service
        const payload = {
          company: params.company,
          team: params.team,
          quarter: params.quarterName,
          metrics: {
            eNPS: params.metrics.eNPS,
            who5: params.metrics.who5,
            debi: params.metrics.debi,
            participation: params.metrics.responseCount
          },
          who5Questions: params.who5Questions,
          recognition: params.recognition,
          managers: params.managers,
          topStrengths: params.topStrengths,
          topChallenges: params.topChallenges,
          contentGuidelines: RECOMMENDATION_CONTENT_GUIDELINES
        };

        // Call AI service
        const axios = require('axios');
        const url = 'https://api-node.happily.ai/ai/get_quarterly_recommendations';
        // const url = 'http://localhost:80/openai/get_quarterly_recommendations'; // Dev

        const response = await axios.post(url, payload, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000
        });

        if (response.data && response.data.recommendations) {
          recommendationsData.recommendations = normalizeGeneratedRecommendations(response.data.recommendations);
          recommendationsData.generatedAt = response.data.generatedAt || new Date().toISOString();
        }

      } catch (error) {
        console.error('Error fetching AI recommendations:', error);
        // Return empty recommendations on error (slide won't show)
      }

      return recommendationsData;
    },

    // Fetch focus coverage and goal alignment data
    async fetchFocusAlignmentData({ dispatch, rootState, rootGetters }: any, params: {
      teamId: string;
      company: string;
      startDate: Date;
      endDate: Date;
    }) {
      const focusAlignmentData: any = {
        focusCoverage: {
          usersWithFocus: 0,
          totalUsers: 0,
          coverageRate: 0
        },
        focusItems: {
          total: 0,
          open: 0,
          completed: 0,
          completionRate: 0
        },
        goalAlignment: {
          alignmentRate: null,
          alignedItems: 0,
          unalignedItems: 0,
          unknownItems: 0
        },
        goals: {
          total: 0,
          withFocus: 0,
          withoutFocus: 0
        },
        goalDistribution: [] as Array<{
          goalId: string;
          goalName: string;
          teamName: string;
          peopleCount: number;
          focusItems: number;
          completedItems: number;
        }>
      };

      try {
        // Set the date range for the quarter
        const daysDiff = Math.ceil((params.endDate.getTime() - params.startDate.getTime()) / (1000 * 60 * 60 * 24));
        await dispatch('focus/setDateRange', { dateRange: daysDiff, reload: false }, { root: true });

        // Load historical focus data for the team using actual quarter dates
        const teamId = params.teamId === 'company-wide' ? 'All Teams' : params.teamId;
        // await dispatch('focus/loadHistoricalFocusDataForTeam', {
        //   teamId: teamId,
        //   includeChildren: true,
        //   startDate: params.startDate,
        //   endDate: params.endDate
        // }, { root: true });


        await dispatch('focus/getQuarterlyFocusData', {
          startDate: params.startDate,
          endDate: params.endDate,
          teamId: teamId,
          includeChildren: true
        }, { root: true });

        // Get historical summary stats
        const historicalSummary = rootGetters['focus/getQuarterlySummaryStats'](teamId, true);
        const historicalGoalStats = rootGetters['focus/getQuarterlyGoalStats'](teamId, true);
        if (historicalSummary) {
          focusAlignmentData.focusItems = {
            total: historicalSummary.totalUniqueItems || 0,
            open: historicalSummary.openItems || 0,
            completed: historicalSummary.completedItems || 0,
            completionRate: historicalSummary.completionRate || 0
          };

          focusAlignmentData.focusCoverage = {
            usersWithFocus: historicalSummary.usersWithFocus || 0,
            totalUsers: 0, // Will be updated below
            coverageRate: 0
          };

          focusAlignmentData.goalAlignment = {
            alignmentRate: historicalSummary.alignmentRate,
            alignedItems: historicalSummary.alignedItems || 0,
            unalignedItems: historicalSummary.unalignedItems || 0,
            unknownItems: 0
          };
        }

        // Get total users for coverage calculation
        const teamsAll = rootGetters['team/teamsall'] || [];
        if (teamId === 'All Teams') {
          // Count all users from users data
          const users = await fetchUsersForCompany(params.company);
          const activeUsers = Object.values(users).filter((u: any) => u.status !== 'inactive' && u.status !== 'deleted');
          focusAlignmentData.focusCoverage.totalUsers = activeUsers.length;
        } else {
          // Get team members count - include child teams
          const descendantTeamIds = getAllDescendantTeams(teamId, teamsAll);
          const teamIdsToCount = [teamId, ...descendantTeamIds];

          const users = await fetchUsersForCompany(params.company);
          const teamUsers = Object.values(users).filter((u: any) =>
            teamIdsToCount.includes(u.team) &&
            u.status !== 'inactive' &&
            u.status !== 'deleted'
          );
          focusAlignmentData.focusCoverage.totalUsers = teamUsers.length;
        }

        // Calculate coverage rate
        if (focusAlignmentData.focusCoverage.totalUsers > 0) {
          focusAlignmentData.focusCoverage.coverageRate = Math.round(
            (focusAlignmentData.focusCoverage.usersWithFocus / focusAlignmentData.focusCoverage.totalUsers) * 100
          );
        }

        // Load goals data if not already loaded
        await dispatch('goals/getGoalsTeamAI', null, { root: true });

        // Get active goals data only. Deleted goals are absent; archived goals are filtered out.
        const goalsTeamAI = rootGetters['goals/activeGoalsTeamAI'] || rootGetters['goals/goalsTeamAI'] || {};
        console.log('[Quarterly Report] Goals data loaded:', {
          teamCount: Object.keys(goalsTeamAI).length,
          teams: Object.keys(goalsTeamAI)
        });
        console.log('[Quarterly Report] Historical goal stats:', historicalGoalStats);

        // Aggregate goals across team and descendants
        let totalGoals = 0;
        let goalsWithFocus = 0;
        const goalDistribution: Array<{
          goalId: string;
          goalName: string;
          teamName: string;
          peopleCount: number;
          focusItems: number;
          completedItems: number;
          topItems: Array<{
            item: string;
            partnerCount: number;
            status: string;
          }>;
        }> = [];

        const scopedTeamIds = teamId === 'All Teams'
          ? Object.keys(goalsTeamAI)
          : [teamId, ...getAllDescendantTeams(teamId, teamsAll)];

        const {
          totalGoals: scopedTotalGoals,
          activeGoalIds,
          goalNameLookup
        } = buildQuarterlyScopedGoalLookup(goalsTeamAI, scopedTeamIds);

        totalGoals = scopedTotalGoals;

        // Use the quarterly cache here so all goal metrics come from the same dataset.
        // Using the generic historical cache can return stale/empty data for this report.
        const quarterlyFocusCache = rootState.focus && rootState.focus.quarterlyFocusByTeam;
        const allFocusItems = (quarterlyFocusCache && quarterlyFocusCache.allItems) ? quarterlyFocusCache.allItems : [];

        // Load partnership data to get partner counts
        const allPartnerships = (rootState.focus && rootState.focus.allPartnerships) ? rootState.focus.allPartnerships : [];

        // Build a map of item_id to partner count
        const partnerCountByItemId: { [itemId: string]: number } = {};
        allPartnerships.forEach((partnership: any) => {
          const itemId = partnership.item_id;
          if (itemId) {
            partnerCountByItemId[itemId] = (partnerCountByItemId[itemId] || 0) + 1;
          }
        });

        const goalAlignment = calculateQuarterlyGoalAlignment(allFocusItems, activeGoalIds);
        focusAlignmentData.goalAlignment = {
          alignmentRate: goalAlignment.alignmentRate,
          alignedItems: goalAlignment.alignedItems,
          unalignedItems: goalAlignment.unalignedItems,
          unknownItems: goalAlignment.unknownItems
        };

        // Group focus items by active goal_id only
        const focusItemsByGoal: { [goalId: string]: Array<{
          item: string;
          item_id: string;
          status: string;
          partnerCount: number;
        }> } = {};

        allFocusItems.forEach((item: any) => {
          if (item.goal_id && item.goal_id !== 'unmatched' && activeGoalIds.has(item.goal_id)) {
            if (!focusItemsByGoal[item.goal_id]) {
              focusItemsByGoal[item.goal_id] = [];
            }
            // Check if this item text already exists (avoid duplicates)
            const existingItem = focusItemsByGoal[item.goal_id].find((i: any) => i.item === item.item);
            if (!existingItem) {
              focusItemsByGoal[item.goal_id].push({
                item: item.item || '',
                item_id: item.item_id || '',
                status: item.status || 'open',
                partnerCount: partnerCountByItemId[item.item_id] || 0
              });
            } else {
              // If duplicate, keep the one with more partners
              const newPartnerCount = partnerCountByItemId[item.item_id] || 0;
              if (newPartnerCount > existingItem.partnerCount) {
                existingItem.partnerCount = newPartnerCount;
                existingItem.item_id = item.item_id;
                existingItem.status = item.status;
              }
            }
          }
        });

        // Helper to get team name from teamsAll
        const getTeamName = (tid: string): string => {
          const team = teamsAll.find((t: any) => t.key === tid);
          return team ? team.name : tid;
        };
        console.log('[Quarterly Report] Goal name lookup built:', Object.keys(goalNameLookup).length, 'goals');

        // Only include active goals that still exist in the current goal set.
        if (historicalGoalStats && typeof historicalGoalStats === 'object') {
          Object.entries(historicalGoalStats).forEach(([goalId, stats]: [string, any]) => {
            if (stats && stats.peopleCount > 0 && activeGoalIds.has(goalId)) {

              // Look up goal name from goalsTeamAI
              const goalInfo = goalNameLookup[goalId];
              const goalName = goalInfo ? goalInfo.name : `Goal ${goalId.substring(0, 8)}...`;
              const goalTeamId = goalInfo ? goalInfo.teamId : '';

              // Get top focus items for this goal (sorted by partner count, take top 3)
              const goalItems = focusItemsByGoal[goalId] || [];
              const topItems = goalItems
                .sort((a, b) => b.partnerCount - a.partnerCount)
                .slice(0, 3)
                .map(item => ({
                  item: item.item,
                  partnerCount: item.partnerCount,
                  status: item.status
                }));

              goalDistribution.push({
                goalId: goalId,
                goalName: goalName,
                teamName: getTeamName(goalTeamId),
                peopleCount: stats.peopleCount || 0,
                focusItems: stats.totalItems || 0,
                completedItems: stats.doneCount || 0,
                topItems: topItems
              });
            }
          });
        }
        goalsWithFocus = goalDistribution.length;

        // Sort goal distribution by focus items (descending)
        goalDistribution.sort((a, b) => b.focusItems - a.focusItems);

        console.log('[Quarterly Report] Goal distribution:', {
          totalGoals,
          goalsWithFocus,
          distributionCount: goalDistribution.length
        });

        focusAlignmentData.goals = {
          total: totalGoals,
          withFocus: goalsWithFocus,
          withoutFocus: Math.max(totalGoals - goalsWithFocus, 0)
        };
        focusAlignmentData.goalDistribution = goalDistribution;

        console.log('[Quarterly Report] Focus alignment data:', focusAlignmentData);
      } catch (error) {
        console.error('[Quarterly Report] Error fetching focus alignment data:', error);
      }

      return focusAlignmentData;
    },

    // Generate slide deck from report data
    async generateSlides({ commit, state }: any, reportData: any) {
      const slides: SlideData[] = [];
      let slideId = 0;

      // Slide 1: Title Cover
      slides.push({
        id: `slide-${slideId++}`,
        type: 'TitleCover',
        data: {
          heroMetric: reportData.metrics.eNPS.value,
          heroLabel: 'eNPS',
          quarterName: reportData.quarter.name,
          teamName: reportData.teamName,
          companyName: reportData.companyName,
          stats: [
            { label: 'WHO-5', value: reportData.metrics.who5.value, suffix: '' },
            { label: 'DEBI', value: reportData.metrics.debi.value, suffix: '' },
            { label: 'vs Previous', value: reportData.metrics.eNPS.change, suffix: '', isChange: true }
          ]
        }
      });

      // Slide 2: Section Divider - Core Metrics
      slides.push({
        id: `slide-${slideId++}`,
        type: 'SectionDivider',
        data: {
          number: '01',
          title: 'Core Engagement Metrics',
          subtitle: 'Key indicators of employee engagement and well-being'
        }
      });

      // Slide 3: Key Metric - eNPS
      slides.push({
        id: `slide-${slideId++}`,
        type: 'KeyMetric',
        data: {
          label: 'Employee Net Promoter Score',
          value: reportData.metrics.eNPS.value,
          change: reportData.metrics.eNPS.change,
          trend: reportData.metrics.eNPS.trend,
          isNPS: true,
          metricType: 'enps',
          description: 'How likely employees are to recommend your organization as a great place to work'
        }
      });

      // Slide 4: Stat Grid - All Core Metrics (only include metrics with data)
      const dashboardMetrics: any[] = [];

      // Always include eNPS and NPS
      dashboardMetrics.push({
        label: 'eNPS',
        value: reportData.metrics.eNPS.value,
        change: reportData.metrics.eNPS.change,
        trend: reportData.metrics.eNPS.trend,
        isNPS: true,
        color: getScoreColor(reportData.metrics.eNPS.value, 'enps')
      });

      dashboardMetrics.push({
        label: 'NPS',
        value: reportData.metrics.nps.value,
        change: reportData.metrics.nps.change,
        trend: reportData.metrics.nps.trend,
        isNPS: true,
        color: getScoreColor(reportData.metrics.nps.value, 'nps')
      });

      // WHO-5 is an index score (0-100), not a percentage
      if (reportData.metrics.who5.value !== 0 || reportData.metrics.who5.previousValue !== 0) {
        dashboardMetrics.push({
          label: 'WHO-5',
          value: reportData.metrics.who5.value,
          change: reportData.metrics.who5.change,
          trend: reportData.metrics.who5.trend,
          suffix: '',
          isIndex: true,
          color: getScoreColor(reportData.metrics.who5.value, 'who5')
        });
      }

      // Only include DEBI if there's actual data
      if (reportData.metrics.debi.value !== 0 || reportData.metrics.debi.previousValue !== 0) {
        dashboardMetrics.push({
          label: 'DEBI',
          value: reportData.metrics.debi.value,
          change: reportData.metrics.debi.change,
          trend: reportData.metrics.debi.trend,
          suffix: '',
          isIndex: true,
          color: getScoreColor(reportData.metrics.debi.value, 'debi')
        });
      }

      // Only include Strategic Alignment if there's actual data
      if (reportData.metrics.strategicAlignment.value !== 0 || reportData.metrics.strategicAlignment.previousValue !== 0) {
        dashboardMetrics.push({
          label: 'Strategic Alignment',
          value: reportData.metrics.strategicAlignment.value,
          change: reportData.metrics.strategicAlignment.change,
          trend: reportData.metrics.strategicAlignment.trend,
          suffix: '%',
          color: getScoreColor(reportData.metrics.strategicAlignment.value, 'percentage')
        });
      }

      slides.push({
        id: `slide-${slideId++}`,
        type: 'StatGrid',
        data: {
          title: 'Engagement Dashboard',
          subtitle: reportData.teamName,
          metrics: dashboardMetrics
        }
      });

      // Section 02: Wellbeing (if we have WHO-5 data)
      if (reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0)) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'SectionDivider',
          data: {
            number: '02',
            title: 'Wellbeing',
            subtitle: 'Mental health and wellness indicators'
          }
        });

        // KeyMetric - WHO-5 Score
        slides.push({
          id: `slide-${slideId++}`,
          type: 'KeyMetric',
          data: {
            label: 'WHO-5 Wellbeing Index',
            value: reportData.metrics.who5.value,
            change: reportData.metrics.who5.change,
            trend: reportData.metrics.who5.trend,
            isNPS: false,
            isRawNumber: true,
            metricType: 'who5',
            description: 'Overall mental wellbeing score based on the WHO-5 questionnaire. Scores above 52 indicate good wellbeing.'
          }
        });

        // WHO-5 Breakdown
        if (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'WHO5Breakdown',
            data: reportData.who5Breakdown
          });
        }
      }

      // Section: Performance Analysis (dynamic number)
      const perfSectionNum = (reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0)) ? '03' : '02';
      slides.push({
        id: `slide-${slideId++}`,
        type: 'SectionDivider',
        data: {
          number: perfSectionNum,
          title: 'Performance Analysis',
          subtitle: 'Strengths, challenges, and areas for improvement'
        }
      });

      // Slide 6: Highlights Grid
      slides.push({
        id: `slide-${slideId++}`,
        type: 'HighlightsGrid',
        data: {
          strengths: reportData.highlights.strengths,
          challenges: reportData.highlights.challenges,
          priorities: generatePriorities(reportData.highlights)
        }
      });

      // Slide 7: Question Scores - Top Performing
      // Get WHO-5 question IDs to filter them out (they have their own dedicated section)
      const who5QuestionIds = Object.values(QUESTION_SETS.WHO5);

      const nonNPSQuestions = reportData.questionScores
        .filter((q: any) => q.type !== 'NPS' && q.type !== 'eNPS');

      // For top/bottom questions, also exclude WHO-5 questions since they have their own section
      const nonWellbeingQuestions = nonNPSQuestions
        .filter((q: any) => !who5QuestionIds.includes(q.id));

      const topQuestions = nonWellbeingQuestions.slice(0, 6);

      if (topQuestions.length > 0) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'QuestionScores',
          data: {
            title: 'Top Performing Questions',
            subtitle: 'Highest scoring areas this quarter',
            questions: topQuestions
          }
        });
      }

      // Slide 8: Question Scores - Bottom Performing (Questions Needing Attention)
      // Get bottom 6 questions (lowest scores, or scores below 70), excluding WHO-5 questions
      const bottomQuestions = [...nonWellbeingQuestions]
        .reverse()  // Already sorted desc, so reverse gives us lowest first
        .slice(0, 6);

      // Only show if there are questions with scores below 70 or if we have enough data
      if (bottomQuestions.length > 0 && (bottomQuestions[0].score < 70 || nonWellbeingQuestions.length >= 10)) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'QuestionScores',
          data: {
            title: 'Questions Needing Attention',
            subtitle: 'Areas with the most room for improvement',
            questions: bottomQuestions
          }
        });
      }

      // Section: Quarter Highlights (dynamic number)
      const highlightsSectionNum = (reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0)) ? '04' : '03';
      slides.push({
        id: `slide-${slideId++}`,
        type: 'SectionDivider',
        data: {
          number: highlightsSectionNum,
          title: 'Quarter Highlights',
          subtitle: 'Changes and trends compared to previous quarter'
        }
      });

      // Slide 10: Chart with Callouts - Improvements ONLY (max 5)
      const improvements = reportData.highlights.biggestImprovements.slice(0, 5);
      if (improvements.length > 0) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'ChartWithCallouts',
          data: {
            title: 'Key Improvements This Quarter',
            chartType: 'bar',
            improvements: improvements,
            declines: [],  // Only show improvements on this slide
            insights: generateImprovementInsights(improvements, reportData)
          }
        });
      }

      // Slide 11: Chart with Callouts - Declines ONLY (max 5)
      const declines = reportData.highlights.biggestDeclines.slice(0, 5);
      if (declines.length > 0) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'ChartWithCallouts',
          data: {
            title: 'Areas of Decline',
            chartType: 'bar',
            improvements: [],  // Only show declines on this slide
            declines: declines,
            insights: generateDeclineInsights(declines, reportData)
          }
        });
      }

      // Manager Effectiveness Section (only if we have manager data)
      if (reportData.managerEffectiveness && reportData.managerEffectiveness.topManagers && reportData.managerEffectiveness.topManagers.length > 0) {
        // Dynamic section number based on whether wellbeing section was shown
        const hasWellbeing = reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0);
        const managerSectionNum = hasWellbeing ? '05' : '04';

        // Section Divider - Manager Effectiveness
        slides.push({
          id: `slide-${slideId++}`,
          type: 'SectionDivider',
          data: {
            number: managerSectionNum,
            title: 'Manager Effectiveness',
            subtitle: 'Leadership performance and team support metrics'
          }
        });

        // KeyMetric - Total Manager Conversations (if available)
        if (reportData.managerEffectiveness.totalConversations && reportData.managerEffectiveness.totalConversations > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'KeyMetric',
            data: {
              label: 'Manager Conversations',
              value: reportData.managerEffectiveness.totalConversations,
              change: 0,
              trend: 'neutral',
              isNPS: false,
              description: 'Total manager replies and conversations with team members this quarter'
            }
          });
        }

        // StatGrid - Manager Metrics Overview
        const managerMetrics: any[] = [];
        if (reportData.managerEffectiveness.avgProactivity > 0) {
          managerMetrics.push({
            label: 'Avg Proactivity',
            value: reportData.managerEffectiveness.avgProactivity,
            suffix: '%',
            color: getScoreColor(reportData.managerEffectiveness.avgProactivity, 'percentage')
          });
        }
        if (reportData.managerEffectiveness.avgPsychSafety > 0) {
          managerMetrics.push({
            label: 'Psych Safety',
            value: reportData.managerEffectiveness.avgPsychSafety,
            suffix: '%',
            color: getScoreColor(reportData.managerEffectiveness.avgPsychSafety, 'percentage')
          });
        }
        // Add DEBI score - Dynamic Engagement Behavior Index
        // DEBI thresholds: Elite ≥60, Effective 50-59, Rising 40-49, At Risk <40
        if (reportData.managerEffectiveness.avgDEBI > 0) {
          managerMetrics.push({
            label: 'Avg DEBI',
            value: reportData.managerEffectiveness.avgDEBI,
            suffix: '',
            color: reportData.managerEffectiveness.avgDEBI >= 60 ? 'green' :
                   reportData.managerEffectiveness.avgDEBI >= 50 ? 'blue' :
                   reportData.managerEffectiveness.avgDEBI >= 40 ? 'yellow' : 'red'
          });
        }
        managerMetrics.push({
          label: 'Active Managers',
          value: reportData.managerEffectiveness.activePercent,
          suffix: '%',
          color: getScoreColor(reportData.managerEffectiveness.activePercent, 'percentage')
        });
        managerMetrics.push({
          label: 'Total Managers',
          value: reportData.managerEffectiveness.totalManagers,
          suffix: '',
          color: 'violet'
        });

        slides.push({
          id: `slide-${slideId++}`,
          type: 'StatGrid',
          data: {
            title: 'Manager Overview',
            subtitle: 'Performance metrics for managers this quarter',
            metrics: managerMetrics
          }
        });

        // Leaderboard - Top Managers
        slides.push({
          id: `slide-${slideId++}`,
          type: 'Leaderboard',
          data: {
            title: 'Top Performing Managers',
            subtitle: 'Ranked by effectiveness score',
            leaders: reportData.managerEffectiveness.topManagers
          }
        });
      }

      // Talent & Culture Leaders Section (only if we have culture data)
      const hasGivers = reportData.cultureLeaders && reportData.cultureLeaders.topGivers && reportData.cultureLeaders.topGivers.length > 0;
      const hasReceivers = reportData.cultureLeaders && reportData.cultureLeaders.topReceivers && reportData.cultureLeaders.topReceivers.length > 0;

      if (hasGivers || hasReceivers) {
        // Determine section number dynamically
        const hasWellbeing = reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0);
        const hasManagers = reportData.managerEffectiveness && reportData.managerEffectiveness.topManagers && reportData.managerEffectiveness.topManagers.length > 0;
        let cultureSectionNum = 4; // Base after Performance Analysis and Quarter Highlights
        if (hasWellbeing) cultureSectionNum++;
        if (hasManagers) cultureSectionNum++;
        const cultureSectionNumStr = String(cultureSectionNum).padStart(2, '0');

        // Section Divider - Culture Leaders
        slides.push({
          id: `slide-${slideId++}`,
          type: 'SectionDivider',
          data: {
            number: cultureSectionNumStr,
            title: 'Recognition & Culture',
            subtitle: 'Peer recognition activity and values alignment'
          }
        });

        // KeyMetric - Total Recognitions
        if (reportData.cultureLeaders.totalRecognitions && reportData.cultureLeaders.totalRecognitions > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'KeyMetric',
            data: {
              label: 'Recognition Moments',
              value: reportData.cultureLeaders.totalRecognitions,
              change: 0,
              trend: 'neutral',
              isNPS: false,
              isRawNumber: true,
              description: 'Peer-to-peer recognition moments shared this quarter'
            }
          });
        }

        // StatGrid - Recognition Overview
        const cultureMetrics: any[] = [];
        cultureMetrics.push({
          label: 'Total Recognitions',
          value: reportData.cultureLeaders.totalRecognitions,
          suffix: '',
          color: 'violet',
          description: 'Peer-to-peer recognitions sent'
        });
        cultureMetrics.push({
          label: 'Unique Recognizers',
          value: reportData.cultureLeaders.uniqueRecognizers,
          suffix: '',
          color: 'emerald',
          description: 'People who gave recognition'
        });
        if (reportData.cultureLeaders.avgAlignmentScore > 0) {
          // Get alignment benchmark description
          const alignmentScore = reportData.cultureLeaders.avgAlignmentScore;
          let alignmentBenchmark = '';
          let alignmentColor = 'emerald';
          if (alignmentScore >= 9) {
            alignmentBenchmark = 'Outstanding';
            alignmentColor = 'emerald';
          } else if (alignmentScore >= 8) {
            alignmentBenchmark = 'Great';
            alignmentColor = 'emerald';
          } else if (alignmentScore >= 7) {
            alignmentBenchmark = 'Good';
            alignmentColor = 'amber';
          } else if (alignmentScore >= 5) {
            alignmentBenchmark = 'Needs Improvement';
            alignmentColor = 'amber';
          } else {
            alignmentBenchmark = 'Needs Attention';
            alignmentColor = 'rose';
          }

          cultureMetrics.push({
            label: 'Avg Alignment',
            value: alignmentScore,
            suffix: '',
            isIndex: true,
            color: alignmentColor,
            description: alignmentBenchmark
          });
        }

        slides.push({
          id: `slide-${slideId++}`,
          type: 'StatGrid',
          data: {
            title: 'Recognition Overview',
            subtitle: 'Culture and recognition metrics this quarter',
            metrics: cultureMetrics
          }
        });

        // Culture Index Questions Slide (only if there are custom culture questions)
        if (reportData.cultureQuestionScores && reportData.cultureQuestionScores.length > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'QuestionScores',
            data: {
              title: 'Culture Index Questions',
              subtitle: 'How employees score on culture-related questions',
              questions: reportData.cultureQuestionScores
            }
          });
        }

        // Leaderboard - Top Recognition Givers
        if (hasGivers) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'Leaderboard',
            data: {
              title: 'Top Recognition Givers',
              subtitle: 'Most active in recognizing peers',
              leaders: reportData.cultureLeaders.topGivers
            }
          });
        }

        // Leaderboard - Top Recognition Receivers
        if (hasReceivers) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'Leaderboard',
            data: {
              title: 'Top Recognition Receivers',
              subtitle: 'Most recognized by peers',
              leaders: reportData.cultureLeaders.topReceivers
            }
          });
        }
      }

      // Section Divider - Summary (only if we have response data)
      // Update section number dynamically
      const summarySecNum = (() => {
        let num = 4; // Base after Performance Analysis and Quarter Highlights
        const hasWellbeing = reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0);
        if (hasWellbeing) num++;
        if (reportData.managerEffectiveness && reportData.managerEffectiveness.topManagers && reportData.managerEffectiveness.topManagers.length > 0) num++;
        const hasCultureData = reportData.cultureLeaders && (
          (reportData.cultureLeaders.topGivers && reportData.cultureLeaders.topGivers.length > 0) ||
          (reportData.cultureLeaders.topReceivers && reportData.cultureLeaders.topReceivers.length > 0)
        );
        if (hasCultureData) num++;
        return String(num).padStart(2, '0');
      })();

      if (reportData.metrics.responseCount && reportData.metrics.responseCount.value > 0) {
        slides.push({
          id: `slide-${slideId++}`,
          type: 'SectionDivider',
          data: {
            number: summarySecNum,
            title: 'Response Summary',
            subtitle: 'Participation and engagement metrics'
          }
        });

        // Slide 13: Response Summary Stat Grid
        const summaryMetrics: any[] = [];

        if (reportData.metrics.responseCount.value > 0) {
          summaryMetrics.push({
            label: 'Total Responses',
            value: reportData.metrics.responseCount.value,
            change: reportData.metrics.responseCount.change,
            trend: reportData.metrics.responseCount.trend,
            suffix: '',
            color: 'violet'
          });
        }

        // Add question count
        summaryMetrics.push({
          label: 'Questions Analyzed',
          value: reportData.questionScores.length,
          suffix: '',
          color: 'violet'
        });

        // Add improvement count
        summaryMetrics.push({
          label: 'Areas Improved',
          value: reportData.highlights.biggestImprovements.length,
          suffix: '',
          color: 'emerald'
        });

        // Add decline count
        if (reportData.highlights.biggestDeclines.length > 0) {
          summaryMetrics.push({
            label: 'Areas Declined',
            value: reportData.highlights.biggestDeclines.length,
            suffix: '',
            color: 'rose'
          });
        }

        if (summaryMetrics.length > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'StatGrid',
            data: {
              title: 'Quarter at a Glance',
              subtitle: reportData.quarter.name,
              metrics: summaryMetrics
            }
          });
        }
      }

      // Section: Focus & Goal Alignment (conditional on having focus data)
      const hasFocusData = reportData.focusAlignment && reportData.focusAlignment.focusItems.total > 0;
      if (hasFocusData) {
        // Calculate section number dynamically
        const focusSecNum = (() => {
          let num = 4; // Base after Performance Analysis and Quarter Highlights
          const hasWellbeing = reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0);
          if (hasWellbeing) num++;
          if (reportData.managerEffectiveness && reportData.managerEffectiveness.topManagers && reportData.managerEffectiveness.topManagers.length > 0) num++;
          const hasCultureData = reportData.cultureLeaders && (
            (reportData.cultureLeaders.topGivers && reportData.cultureLeaders.topGivers.length > 0) ||
            (reportData.cultureLeaders.topReceivers && reportData.cultureLeaders.topReceivers.length > 0)
          );
          if (hasCultureData) num++;
          if (reportData.metrics.responseCount && reportData.metrics.responseCount.value > 0) num++;
          return String(num).padStart(2, '0');
        })();

        // SectionDivider - Focus & Goal Alignment
        slides.push({
          id: `slide-${slideId++}`,
          type: 'SectionDivider',
          data: {
            number: focusSecNum,
            title: 'Focus & Goal Alignment',
            subtitle: 'Team productivity and goal alignment metrics'
          }
        });

        // KeyMetric - Focus Coverage
        slides.push({
          id: `slide-${slideId++}`,
          type: 'KeyMetric',
          data: {
            label: 'Focus Coverage',
            value: reportData.focusAlignment.focusCoverage.coverageRate,
            change: 0,
            trend: 'neutral',
            isNPS: false,
            description: `${reportData.focusAlignment.focusCoverage.usersWithFocus} of ${reportData.focusAlignment.focusCoverage.totalUsers} team members have active focus items`
          }
        });

        // StatGrid - Focus Items Overview
        slides.push({
          id: `slide-${slideId++}`,
          type: 'StatGrid',
          data: {
            title: 'Focus Items Overview',
            subtitle: 'Focus activity this quarter',
            metrics: [
              { label: 'Total Items', value: reportData.focusAlignment.focusItems.total, suffix: '' },
              { label: 'In Progress', value: reportData.focusAlignment.focusItems.open, suffix: '', color: 'violet' },
              { label: 'Completed', value: reportData.focusAlignment.focusItems.completed, suffix: '', color: 'emerald' },
              { label: 'Completion Rate', value: reportData.focusAlignment.focusItems.completionRate, suffix: '%' }
            ]
          }
        });

        // KeyMetric - Goal Alignment (only if alignment data is applicable)
        if (reportData.focusAlignment.goalAlignment.alignmentRate !== null) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'KeyMetric',
            data: {
              label: 'Goal Alignment',
              value: reportData.focusAlignment.goalAlignment.alignmentRate,
              change: 0,
              trend: 'neutral',
              isNPS: false,
              description: `${reportData.focusAlignment.goalAlignment.alignedItems} focus items linked to organizational goals`
            }
          });
        }

        // StatGrid - Goals Overview (only if team has goals)
        if (reportData.focusAlignment.goals.total > 0) {
          slides.push({
            id: `slide-${slideId++}`,
            type: 'StatGrid',
            data: {
              title: 'Goals Overview',
              subtitle: 'Goal focus distribution',
              metrics: [
                { label: 'Total Goals', value: reportData.focusAlignment.goals.total, suffix: '' },
                { label: 'With Focus', value: reportData.focusAlignment.goals.withFocus, suffix: '', color: 'emerald' },
                { label: 'Without Focus', value: reportData.focusAlignment.goals.withoutFocus, suffix: '', color: 'amber' },
                { label: 'Aligned Items', value: reportData.focusAlignment.goalAlignment.alignedItems, suffix: '', color: 'violet' }
              ]
            }
          });
        }

        // FocusAlignment - Combined alignment and goal distribution visualization
        const hasAlignmentData = reportData.focusAlignment.goalAlignment.alignedItems > 0 ||
                                 reportData.focusAlignment.goalAlignment.unalignedItems > 0 ||
                                 reportData.focusAlignment.goalAlignment.unknownItems > 0 ||
                                 (reportData.focusAlignment.goalDistribution && reportData.focusAlignment.goalDistribution.length > 0);

        if (hasAlignmentData) {
          const topGoals = (reportData.focusAlignment.goalDistribution || []).slice(0, 5);
          slides.push({
            id: `slide-${slideId++}`,
            type: 'FocusAlignment',
            data: {
              title: 'Focus by Goal',
              subtitle: 'Top goals by focus activity this quarter',
              unalignedItems: reportData.focusAlignment.goalAlignment.unalignedItems,
              unknownItems: reportData.focusAlignment.goalAlignment.unknownItems,
              goals: topGoals
            }
          });
        }
      }

      // AI Recommendations Section (always shown - component has placeholder content)
      // Calculate section number (includes Focus section if present)
      const recsSecNum = (() => {
        let num = 4; // Base
        const hasWellbeing = reportData.metrics.who5.value > 0 || (reportData.who5Breakdown && reportData.who5Breakdown.questions && reportData.who5Breakdown.questions.length > 0);
        if (hasWellbeing) num++;
        if (reportData.managerEffectiveness && reportData.managerEffectiveness.topManagers && reportData.managerEffectiveness.topManagers.length > 0) num++;
        const hasCultureData = reportData.cultureLeaders && (
          (reportData.cultureLeaders.topGivers && reportData.cultureLeaders.topGivers.length > 0) ||
          (reportData.cultureLeaders.topReceivers && reportData.cultureLeaders.topReceivers.length > 0)
        );
        if (hasCultureData) num++;
        if (reportData.metrics.responseCount && reportData.metrics.responseCount.value > 0) num++;
        // Include Focus & Goal Alignment section
        if (reportData.focusAlignment && reportData.focusAlignment.focusItems.total > 0) num++;
        return String(num).padStart(2, '0');
      })();

      slides.push({
        id: `slide-${slideId++}`,
        type: 'SectionDivider',
        data: {
          number: recsSecNum,
          title: 'Recommendations',
          subtitle: 'AI-powered insights and action items'
        }
      });

      slides.push({
        id: `slide-${slideId++}`,
        type: 'Recommendations',
        data: reportData.recommendations || { recommendations: [] }
      });

      // Final Slide: Closing
      slides.push({
        id: `slide-${slideId++}`,
        type: 'Closing',
        data: {
          title: 'Thank You',
          subtitle: 'Your feedback drives positive change.',
          nextSteps: [
            'Review detailed results with leadership',
            'Develop action plans for key areas',
            'Communicate progress to all employees',
            'Schedule follow-up surveys to track improvement'
          ],
          companyName: reportData.companyName,
          generatedAt: reportData.generatedAt
        }
      });

      commit('setSlides', slides);
    },

    // Navigation actions
    nextSlide({ commit, state }: any) {
      if (state.currentSlideIndex < state.slides.length - 1) {
        commit('setCurrentSlideIndex', state.currentSlideIndex + 1);
      }
    },
    prevSlide({ commit, state }: any) {
      if (state.currentSlideIndex > 0) {
        commit('setCurrentSlideIndex', state.currentSlideIndex - 1);
      }
    },
    goToSlide({ commit, state }: any, index: number) {
      if (index >= 0 && index < state.slides.length) {
        commit('setCurrentSlideIndex', index);
      }
    },
    toggleFullscreen({ commit, state }: any) {
      commit('setIsFullscreen', !state.isFullscreen);
    },

    // Save a generated report to Firestore (split into metadata and data collections)
    async saveReport({ commit, dispatch, state }: any, params: {
      reportData: any;
      slides: any[];
      teamName: string;
      quarter: { id: string; name: string; year: number; quarter: number };
      company: string;
      generatedBy: string;
    }) {
      const db = firebase.firestore();
      const teamId = params.reportData.team || 'company-wide';
      const docId = `${params.company}_${teamId}_${params.quarter.id}`;

      try {
        const metadataRef = db.collection('generated_reports_saved').doc(docId);
        const dataRef = db.collection('generated_reports_data').doc(docId);

        const existingDoc = await metadataRef.get();
        const now = new Date().toISOString();

        const metadata = {
          company: params.company,
          teamId: teamId,
          teamName: params.teamName,
          quarterId: params.quarter.id,
          quarterName: params.quarter.name,
          year: params.quarter.year,
          quarter: params.quarter.quarter,
          reportName: `${params.teamName} - ${params.quarter.name}`,
          generatedBy: params.generatedBy,
          updatedAt: now
        };

        const reportDataPayload = {
          company: params.company,
          teamId: teamId,
          quarterId: params.quarter.id,
          reportData: params.reportData,
          slides: params.slides,
          updatedAt: now
        };

        if (existingDoc.exists) {
          // Update existing documents
          await Promise.all([
            metadataRef.update(metadata),
            dataRef.update(reportDataPayload)
          ]);
          console.log('[Quarterly Report] Updated existing saved report:', docId);
        } else {
          // Create new documents
          await Promise.all([
            metadataRef.set({ ...metadata, createdAt: now }),
            dataRef.set(reportDataPayload)
          ]);
          console.log('[Quarterly Report] Created new saved report:', docId);
        }

        // Refresh the saved reports list after saving
        dispatch('fetchSavedReports');
      } catch (error) {
        console.error('[Quarterly Report] Error saving report:', error);
      }
    },

    // Fetch all saved reports for the company (metadata only for fast loading)
    async fetchSavedReports({ commit, rootState }: any) {
      const db = firebase.firestore();
      commit('setSavedReportsLoading', true);

      try {
        const company = rootState.authen.user.company;
        const snapshot = await db.collection('generated_reports_saved')
          .where('company', '==', company)
          .get();

        const reports: SavedReportMetadata[] = [];
        snapshot.forEach((doc: any) => {
          const data = doc.data();
          reports.push({
            id: doc.id,
            company: data.company,
            teamId: data.teamId,
            teamName: data.teamName,
            quarterId: data.quarterId,
            quarterName: data.quarterName,
            year: data.year,
            quarter: data.quarter,
            reportName: data.reportName,
            createdAt: data.createdAt,
            updatedAt: data.updatedAt,
            generatedBy: data.generatedBy
          });
        });

        // Sort by updatedAt descending (most recent first)
        reports.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

        commit('setSavedReports', reports);
        console.log('[Quarterly Report] Fetched saved reports:', reports.length);
      } catch (error) {
        console.error('[Quarterly Report] Error fetching saved reports:', error);
      } finally {
        commit('setSavedReportsLoading', false);
      }
    },

    // Delete a saved report (both metadata and data documents)
    async deleteReport({ commit, state }: any, reportId: string) {
      const db = firebase.firestore();

      try {
        // Delete both metadata and data documents
        await Promise.all([
          db.collection('generated_reports_saved').doc(reportId).delete(),
          db.collection('generated_reports_data').doc(reportId).delete()
        ]);

        // Remove from local state
        const updatedReports = state.savedReports.filter(
          (r: SavedReportMetadata) => r.id !== reportId
        );
        commit('setSavedReports', updatedReports);

        console.log('[Quarterly Report] Deleted report:', reportId);
      } catch (error) {
        console.error('[Quarterly Report] Error deleting report:', error);
        throw error;
      }
    },

    // Load a saved report's full data for viewing
    async loadSavedReport({ commit, state }: any, reportId: string) {
      const db = firebase.firestore();
      commit('setLoading', true);
      commit('setLoadingProgress', 50);

      try {
        // Find the metadata from state
        const metadata = state.savedReports.find((r: SavedReportMetadata) => r.id === reportId);
        if (!metadata) {
          throw new Error('Report metadata not found');
        }

        // Fetch the full report data
        const dataDoc = await db.collection('generated_reports_data').doc(reportId).get();
        if (!dataDoc.exists) {
          throw new Error('Report data not found');
        }

        const data = dataDoc.data();
        commit('setLoadingProgress', 80);

        if (!data) {
          throw new Error('Report data is empty');
        }

        // Set the report data and slides
        commit('setReportData', data.reportData);
        commit('setSlides', data.slides);
        commit('setSelectedQuarter', {
          id: metadata.quarterId,
          name: metadata.quarterName,
          year: metadata.year,
          quarter: metadata.quarter
        });
        commit('setSelectedTeam', metadata.teamId);
        commit('setCurrentSlideIndex', 0);
        commit('setLoadingProgress', 100);
        commit('setReportGenerated', true);
        commit('setIsViewingSavedReport', true);

        console.log('[Quarterly Report] Loaded saved report:', reportId);
      } catch (error) {
        console.error('[Quarterly Report] Error loading saved report:', error);
      } finally {
        commit('setLoading', false);
      }
    }
  },

  getters: {
    selectedQuarter: (state: QuarterlyReportState) => state.selectedQuarter,
    previousQuarter: (state: QuarterlyReportState) => state.previousQuarter,
    selectedTeam: (state: QuarterlyReportState) => state.selectedTeam,
    reportData: (state: QuarterlyReportState) => state.reportData,
    slides: (state: QuarterlyReportState) => state.slides,
    isLoading: (state: QuarterlyReportState) => state.isLoading,
    loadingProgress: (state: QuarterlyReportState) => state.loadingProgress,
    currentSlideIndex: (state: QuarterlyReportState) => state.currentSlideIndex,
    currentSlide: (state: QuarterlyReportState) => state.slides[state.currentSlideIndex] || null,
    totalSlides: (state: QuarterlyReportState) => state.slides.length,
    isFullscreen: (state: QuarterlyReportState) => state.isFullscreen,
    isReportGenerated: (state: QuarterlyReportState) => state.isReportGenerated,
    // Saved reports getters
    savedReports: (state: QuarterlyReportState) => state.savedReports,
    savedReportsLoading: (state: QuarterlyReportState) => state.savedReportsLoading,
    isViewingSavedReport: (state: QuarterlyReportState) => state.isViewingSavedReport
  }
};

// Helper functions

// Calculate NPS score using specific question IDs
function calculateNPSScoreByIds(data: any, questionIds: string[]): number {
  let promoters = 0;
  let detractors = 0;
  let total = 0;

  questionIds.forEach(questId => {
    if (data[questId]) {
      data[questId].forEach((record: any) => {
        if (record.answer) {
          Object.entries(record.answer).forEach(([score, count]: [string, any]) => {
            const scoreNum = parseInt(score);
            const countNum = Number(count);
            total += countNum;
            if (scoreNum >= 9) promoters += countNum;
            else if (scoreNum <= 6) detractors += countNum;
          });
        }
      });
    }
  });

  if (total === 0) return 0;
  return Math.round(((promoters - detractors) / total) * 100);
}

// Legacy function - kept for compatibility
function calculateNPSScore(data: any, type: string): number {
  let promoters = 0;
  let detractors = 0;
  let total = 0;

  Object.entries(data).forEach(([questId, records]: [string, any]) => {
    records.forEach((record: any) => {
      if (record.type === type || (type === 'eNPS' && record.nps === 'eNPS') || (type === 'NPS' && record.nps === 'Y')) {
        if (record.answer) {
          Object.entries(record.answer).forEach(([score, count]: [string, any]) => {
            const scoreNum = parseInt(score);
            const countNum = Number(count);
            total += countNum;
            if (scoreNum >= 9) promoters += countNum;
            else if (scoreNum <= 6) detractors += countNum;
          });
        }
      }
    });
  });

  if (total === 0) return 0;
  return Math.round(((promoters - detractors) / total) * 100);
}

// WHO-5 calculation using weighted scoring (same as Report Card)
// Stored option 0 means "All of the time" and stored option 5 means
// "At no time", so the stored option order is best-to-worst.
function calculateWHO5Score(data: any, questionIds: string[]): number {
  let dataSum = 0;
  let maximum = 0;

  questionIds.forEach(questId => {
    if (data[questId]) {
      data[questId].forEach((record: any) => {
        if (record.answer && typeof record.answer === 'object') {
          WHO5_RESPONSE_BUCKETS.forEach(bucket => {
            const count = Number(record.answer[bucket.answerKey] || 0);
            if (count > 0) {
              dataSum += count * bucket.scoreWeight;
              maximum += count * 5;
            }
          });
        }
      });
    }
  });

  if (maximum === 0) return 0;
  return Math.round((dataSum * 100) / maximum * 10) / 10;  // One decimal place like Report Card
}

// Strategic Alignment categories (same as Report Card)
const STRATEGIC_ALIGNMENT_CATEGORIES = {
  UNDERSTANDING: {
    name: 'Understanding & Clarity',
    questions: ['-OGSfTQqu71fi7f67Ztm', '-OGSgDcIZeg9vJ6JTUYJ'],
    weight: 0.25
  },
  EXECUTION: {
    name: 'Execution & Confidence',
    questions: ['-OGSh6nBCPhmZVVcSvO6', '-OGSi-yigX4U4lSn8z7h'],
    weight: 0.25
  },
  RESOURCES: {
    name: 'Resource & Support',
    questions: ['-OGSiV8s4nrPMhxK_zAe', '-OGSj6iv4BiFfUlwg290'],
    weight: 0.25
  },
  IMPACT: {
    name: 'Impact & Contribution',
    questions: ['-OGSjePG4WFU8A4YH0iB', '-OGSmPA41e8em6xyRJ-5'],
    weight: 0.25
  }
};

// Strategic Alignment calculation using weightMap [4, 3, 2, 1, 0] (same as Report Card)
function calculateStrategicAlignmentScore(data: any, questionIds: string[]): number {
  const weightMap = [4, 3, 2, 1, 0];  // Score for each answer option (0=4 points, 4=0 points)

  // First, calculate weighted average for each question
  const questionAverages: { [key: string]: number } = {};

  questionIds.forEach(questId => {
    if (data[questId]) {
      let totalScore = 0;
      let totalCount = 0;

      data[questId].forEach((record: any) => {
        if (record.answer && typeof record.answer === 'object') {
          // answer is an object like { "0": 5, "1": 3 } where key is option, value is count
          Object.entries(record.answer).forEach(([option, count]: [string, any]) => {
            const optionNum = Number(option);
            const countNum = Number(count);
            if (optionNum >= 0 && optionNum <= 4 && countNum > 0) {
              totalScore += weightMap[optionNum] * countNum;
              totalCount += countNum;
            }
          });
        }
      });

      if (totalCount > 0) {
        questionAverages[questId] = totalScore / totalCount;
      }
    }
  });

  if (Object.keys(questionAverages).length === 0) return 0;

  // Calculate category scores and overall index (same as calculateMetrics in report.ts)
  let weightedSum = 0;
  let totalWeight = 0;

  Object.values(STRATEGIC_ALIGNMENT_CATEGORIES).forEach((category: any) => {
    const categoryResponses = category.questions
      .filter((q: string) => questionAverages[q] !== undefined)
      .map((q: string) => questionAverages[q]);

    if (categoryResponses.length > 0) {
      const score = (categoryResponses.reduce((sum: number, r: number) => sum + r, 0) / categoryResponses.length) * 25;
      weightedSum += score;
      totalWeight += 100;
    }
  });

  if (totalWeight === 0) return 0;
  return Math.round((weightedSum / totalWeight) * 100);
}

// DEBI component weights and max values
const DEBI_COMPONENT_WEIGHTS: { [key: string]: number } = {
  'DPSRRS': 0.34,
  'OFRS': 0.22,
  'OFES': 0.11,
  'RPRS': 0.22,
  'VAS': 0.11
};

const DEBI_COMPONENT_MAX: { [key: string]: number } = {
  'DPSRRS': 1,
  'OFRS': 0.8,
  'OFES': 1,
  'RPRS': 1,
  'VAS': 0.8
};

async function fetchDEBIMetrics(params: { quarterId: string; prevQuarterId: string; team: string; rootTeam?: string | null; company: string }): Promise<MetricValue> {
  const db = firebase.firestore();
  let currentScore = 0;
  let prevScore = 0;

  // Determine which team to filter by (rootTeam for company-wide, or selected team)
  const teamFilter = params.team === 'company-wide' ? params.rootTeam : params.team;

  try {
    // Fetch current quarter DEBI components (PG-first)
    const currentSnapshot = (await pgQuarter({
      company: params.company,
      quarter: Number(params.quarterId),
      ...(teamFilter ? { scope_type: 'team', scope_id: teamFilter } : {}),
    })) || emptySnap;

    // Group by team/cohort to calculate DEBI per team then average
    const currentByTeam: { [key: string]: any[] } = {};

    currentSnapshot.forEach((doc: any) => {
      const data = doc.data();
      const teamKey = data.team || data.cohort || 'all';
      if (!currentByTeam[teamKey]) {
        currentByTeam[teamKey] = [];
      }
      currentByTeam[teamKey].push(data);
    });

    // Calculate DEBI for each team
    const currentDEBIScores: number[] = [];
    Object.values(currentByTeam).forEach(teamData => {
      const debi = calculateDEBIFromComponents(teamData);
      if (debi !== null) {
        currentDEBIScores.push(debi);
      }
    });

    if (currentDEBIScores.length > 0) {
      currentScore = Math.round((currentDEBIScores.reduce((a, b) => a + b, 0) / currentDEBIScores.length) * 10) / 10;
    }

    // Fetch previous quarter DEBI components (PG-first)
    const prevSnapshot = (await pgQuarter({
      company: params.company,
      quarter: Number(params.prevQuarterId),
      ...(teamFilter ? { scope_type: 'team', scope_id: teamFilter } : {}),
    })) || emptySnap;

    // Group by team/cohort
    const prevByTeam: { [key: string]: any[] } = {};

    prevSnapshot.forEach((doc: any) => {
      const data = doc.data();
      const teamKey = data.team || data.cohort || 'all';
      if (!prevByTeam[teamKey]) {
        prevByTeam[teamKey] = [];
      }
      prevByTeam[teamKey].push(data);
    });

    // Calculate DEBI for each team
    const prevDEBIScores: number[] = [];
    Object.values(prevByTeam).forEach(teamData => {
      const debi = calculateDEBIFromComponents(teamData);
      if (debi !== null) {
        prevDEBIScores.push(debi);
      }
    });

    if (prevDEBIScores.length > 0) {
      prevScore = Math.round((prevDEBIScores.reduce((a, b) => a + b, 0) / prevDEBIScores.length) * 10) / 10;
    }
  } catch (error) {
    console.error('Error fetching DEBI metrics:', error);
  }

  return {
    value: currentScore,
    previousValue: prevScore,
    change: currentScore - prevScore,
    trend: currentScore > prevScore ? 'up' : currentScore < prevScore ? 'down' : 'neutral'
  };
}

// Calculate DEBI from component data
function calculateDEBIFromComponents(componentData: any[]): number | null {
  let totalWeightedScore = 0;
  let componentsFound = 0;

  Object.keys(DEBI_COMPONENT_WEIGHTS).forEach(componentType => {
    const component = componentData.find((d: any) => d.type === componentType);
    if (component && component.answer !== undefined && component.total !== undefined && component.total > 0) {
      let score = component.answer / component.total;
      const max = DEBI_COMPONENT_MAX[componentType];
      const weight = DEBI_COMPONENT_WEIGHTS[componentType];

      // Normalize score
      if (score > max) {
        score = 1;
      } else {
        score = score / max;
      }

      totalWeightedScore += score * weight;
      componentsFound++;
    }
  });

  // Only return a score if we found at least some components
  if (componentsFound === 0) {
    return null;
  }

  // Convert to 0-100 scale (the weights sum to 1.0, so multiply by 100)
  return Math.round(totalWeightedScore * 100);
}

function getScoreColor(score: number, type: string): string {
  // eNPS (Employee Net Promoter Score): How likely to recommend company as workplace
  // Scale: -100 to +100
  // Benchmarks based on industry standards
  if (type === 'enps') {
    if (score >= 50) return 'emerald';  // Excellent - highly engaged workforce
    if (score >= 20) return 'amber';    // Good - room for improvement
    return 'rose';                       // Needs attention
  }

  // NPS (Internal Product/Service NPS): Employees rating their own products
  // Scale: -100 to +100
  // Higher expectations because employees should be ambassadors for their products
  if (type === 'nps') {
    if (score >= 50) return 'emerald';  // Good - employees believe in products
    if (score >= 30) return 'amber';    // Moderate - some concerns
    return 'rose';                       // Poor - employees don't advocate for products
  }

  // WHO-5 (WHO Well-being Index): Clinical well-being measure
  // Scale: 0-100 (percentage of max score)
  // Based on clinical thresholds
  if (type === 'who5') {
    if (score >= 52) return 'emerald';  // Good well-being
    if (score >= 28) return 'amber';    // Moderate - may need support
    return 'rose';                       // Low - potential depression risk
  }

  // DEBI (Dynamic Engagement Behavior Index): Manager engagement behavior
  // Scale: 0-100
  // Based on DEBI classification: Elite ≥60, Effective 50-59, Rising 40-49, At Risk <40
  if (type === 'debi') {
    if (score >= 60) return 'emerald';  // Elite
    if (score >= 40) return 'amber';    // Effective/Rising
    return 'rose';                       // At Risk
  }

  // Strategic Alignment and other percentage-based scores
  // Scale: 0-100%
  if (score >= 70) return 'emerald';
  if (score >= 50) return 'amber';
  return 'rose';
}

function generatePriorities(highlights: any): any[] {
  const priorities: any[] = [];

  // Add challenges as priorities
  highlights.challenges.slice(0, 2).forEach((c: any) => {
    priorities.push({
      label: `Improve ${c.label}`,
      score: c.score,
      type: 'challenge'
    });
  });

  // Add action items based on declines
  highlights.biggestDeclines.slice(0, 1).forEach((d: any) => {
    priorities.push({
      label: `Address ${d.name} decline`,
      change: d.change,
      type: 'decline'
    });
  });

  return priorities.slice(0, 3);
}

function generateInsights(reportData: any): string[] {
  const insights: string[] = [];

  // eNPS insight
  if (reportData.metrics.eNPS.trend === 'up') {
    insights.push(`eNPS improved by ${reportData.metrics.eNPS.change} points`);
  } else if (reportData.metrics.eNPS.trend === 'down') {
    insights.push(`eNPS declined by ${Math.abs(reportData.metrics.eNPS.change)} points`);
  }

  // Strengths insight
  if (reportData.highlights.strengths.length > 0) {
    insights.push(`${reportData.highlights.strengths.length} areas scoring above 70%`);
  }

  // Challenges insight
  if (reportData.highlights.challenges.length > 0) {
    insights.push(`${reportData.highlights.challenges.length} areas need attention (below 50%)`);
  }

  return insights;
}

// Generate insights specific to improvements
function generateImprovementInsights(improvements: any[], reportData: any): string[] {
  const insights: string[] = [];

  if (improvements.length === 0) return insights;

  // Highlight the biggest improvement
  const biggest = improvements[0];
  insights.push(`${biggest.name} saw the largest gain (+${biggest.change})`);

  // Count core vs question improvements
  const coreImprovements = improvements.filter((i: any) => i.type === 'core');
  const questionImprovements = improvements.filter((i: any) => i.type === 'question');

  if (coreImprovements.length > 0 && questionImprovements.length > 0) {
    insights.push(`${coreImprovements.length} core metrics and ${questionImprovements.length} questions improved`);
  } else if (coreImprovements.length > 0) {
    insights.push(`${coreImprovements.length} core metric${coreImprovements.length > 1 ? 's' : ''} improved`);
  } else if (questionImprovements.length > 0) {
    insights.push(`${questionImprovements.length} question area${questionImprovements.length > 1 ? 's' : ''} improved`);
  }

  // Average improvement
  const avgImprovement = Math.round(improvements.reduce((sum: number, i: any) => sum + i.change, 0) / improvements.length);
  if (avgImprovement > 0) {
    insights.push(`Average improvement of +${avgImprovement} points`);
  }

  return insights;
}

// Generate insights specific to declines
function generateDeclineInsights(declines: any[], reportData: any): string[] {
  const insights: string[] = [];

  if (declines.length === 0) return insights;

  // Highlight the biggest decline
  const biggest = declines[0];
  insights.push(`${biggest.name} needs the most attention (${biggest.change})`);

  // Count core vs question declines
  const coreDeclines = declines.filter((d: any) => d.type === 'core');
  const questionDeclines = declines.filter((d: any) => d.type === 'question');

  if (coreDeclines.length > 0) {
    insights.push(`${coreDeclines.length} core metric${coreDeclines.length > 1 ? 's' : ''} declined`);
  }

  // Provide context about challenges
  if (reportData.highlights.challenges && reportData.highlights.challenges.length > 0) {
    insights.push(`${reportData.highlights.challenges.length} areas scoring below 50%`);
  }

  return insights;
}

// Format name to proper case (handles "JOHN DOE", "john doe", "john.doe@email.com")
function formatProperCase(name: string): string {
  if (!name) return '';

  // If it looks like an email, extract the name part
  if (name.includes('@')) {
    name = name.split('@')[0];
  }

  // Replace dots and underscores with spaces
  name = name.replace(/[._]/g, ' ');

  // Split into words and capitalize each
  return name
    .toLowerCase()
    .split(' ')
    .map(word => {
      if (word.length === 0) return '';
      // Handle names with apostrophes (O'Brien) or hyphens (Mary-Jane)
      return word
        .split(/(['-])/)
        .map(part => {
          if (part === "'" || part === '-') return part;
          return part.charAt(0).toUpperCase() + part.slice(1);
        })
        .join('');
    })
    .filter(word => word.length > 0)
    .join(' ');
}
